/**
 * SCD Manual — pedagogical content for the in-game animated book.
 * Each chapter has flexible pages (variant 2b user choice).
 *
 * Page types:
 *   - 'cover'        : chapter title + intro paragraph
 *   - 'definitions'  : list of key concepts with definitions
 *   - 'formulas'     : key formulas with worked examples
 *   - 'vocabulary'   : alphabetical glossary
 *
 * All numbers / thresholds are aligned with the actual game code so the
 * manual matches what the apprenant experiences in-game.
 */

export const MANUAL_CHAPTERS = [
  // ════════════════════════════════════════════════════════════════════
  // 0. MÉCANIQUES DU RETAIL (SCD)
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'retail',
    title: 'Mécaniques du retail',
    subtitle: 'ISM, facing, parcours client, transformation',
    icon: 'Store',
    color: '#E2704A',
    pages: [
      {
        type: 'cover',
        title: 'Mécaniques du retail',
        intro: "Le commerce de détail obéit à des mécaniques précises : ce que le client voit, le chemin qu'il parcourt, le temps qu'il attend et la marge que vous captez. Ces huit concepts, rencontrés dans vos situations de jeu, sont le socle du métier de commerçant.",
        bullets: [
          'Piloter la marge produit par produit (ISM)',
          'Vendre son emplacement : facing et marges arrière',
          'Faire circuler le client : zones chaudes et froides',
          'Mesurer pour agir : transformation, démarque, saisonnalité',
        ],
      },
      {
        type: 'definitions',
        title: 'Les concepts clés',
        items: [
          { term: "Indice de sensibilité à la marge (ISM)", def: "Compare la part de marge d'une famille de produits à sa part de chiffre d'affaires. ISM > 1 : la famille rapporte plus de marge que de CA — à protéger. ISM < 1 : produit d'appel qui attire mais rapporte peu. On monte les prix des produits « plaisir » peu comparés, jamais des prix repères que le client connaît par cœur." },
          { term: 'Facing', def: "Nombre de produits visibles de face sur l'étagère. Doubler le facing d'une référence augmente ses ventes de 15 à 30 %. Le facing à hauteur des yeux (1,50-1,70 m) est l'emplacement en or : les fournisseurs paient pour l'obtenir (têtes de gondole, marges arrière)." },
          { term: 'Marges arrière', def: "Avantages négociés avec un fournisseur en dehors du prix d'achat : participation publicitaire, remise de coopération, paiement d'emplacements. Un bon acheteur négocie une remise durable plutôt qu'un chèque unique." },
          { term: 'Zones chaudes / zones froides', def: "Les zones chaudes sont naturellement traversées (entrée, caisses, allée centrale) ; les zones froides sont évitées (fond, angles). Placer les produits les plus demandés au fond force le client à traverser tout le magasin — c'est pour cela que le lait est au fond des supermarchés." },
          { term: 'Cross-merchandising', def: "Placer côte à côte des produits complémentaires (pâtes et sauce, chips et boissons) pour déclencher des achats d'impulsion et faire grimper le panier moyen. Se teste sur une zone pilote avant d'être généralisé (test & learn)." },
          { term: 'Démarque inconnue', def: "Écart entre le stock théorique et le stock réel : vols (clients et internes), casse non déclarée, erreurs de caisse ou de réception. Elle coûte en moyenne 1 à 2 % du CA d'un commerce. Se combat par l'inventaire tournant, l'antivol et la formation de l'équipe." },
          { term: 'Taux de transformation', def: "Part des visiteurs qui deviennent acheteurs. C'est LE juge de paix du commerce physique : le trafic mesure l'attractivité, la transformation mesure la performance du magasin (accueil, assortiment, prix, attente). Sans compteur d'entrées, il est impossible à connaître." },
          { term: "File d'attente", def: "Au-delà de 5 minutes d'attente perçue, une part des clients abandonne son panier. La parade : ouvrir des caisses aux heures de pointe (l'organisation avant l'investissement), puis occuper l'attente (PLV, achats d'impulsion en file)." },
          { term: 'Saisonnalité', def: "La plupart des commerces réalisent 20 à 30 % de leur CA annuel sur une courte période (fêtes, rentrée, saison touristique). Se prépare en amont : précommander le stock avant le pic, renforcer l'équipe, puis écouler vite après — un stock saisonnier invendu perd sa valeur." },
          { term: 'Point de commande', def: "Le niveau de stock qui déclenche le réapprovisionnement. Il couvre les ventes pendant le délai de livraison, plus un stock de sécurité pour absorber les pics et les retards fournisseurs. Trop bas : rupture et ventes perdues ; trop haut : trésorerie immobilisée et risque de démarque." },
          { term: 'Consigne de réapprovisionnement', def: "Règle automatique donnée au système d'achats (page Achats du jeu) : famille de produits + stock mini déclencheur + quantité à commander + prix unitaire maximum accepté. Dès que le stock passe sous le seuil, la commande part seule chez le fournisseur le moins cher qui couvre la gamme. Avec la Centrale d'achats groupe, le « pilotage centrale » configure toutes les familles d'un clic (seuil = 10 jours de ventes, quantité = 30 jours)." },
        ],
      },
      {
        type: 'formulas',
        title: 'Formules du retail',
        items: [
          {
            name: 'Indice de sensibilité à la marge (ISM)',
            formula: 'ISM = (marge famille ÷ marge totale) ÷ (CA famille ÷ CA total)',
            example: "Une famille fait 10 % du CA mais 18 % de la marge : ISM = 0,18 ÷ 0,10 = 1,8. Famille très rentable, à mettre en avant et à protéger des promotions.",
          },
          {
            name: 'Taux de transformation',
            formula: 'Transformation = (nombre de tickets ÷ nombre de visiteurs) × 100',
            example: "450 visiteurs comptés, 117 passages en caisse : transformation = 26 %. Gagner 2 points (28 %) à trafic égal = +7,7 % de CA sans dépenser un euro en publicité.",
          },
          {
            name: 'Taux de démarque',
            formula: 'Démarque = (valeur des pertes ÷ CA) × 100',
            example: "18 000 € de pertes constatées à l'inventaire pour 1 000 000 € de CA : démarque = 1,8 %. Passer à 1 % rend 8 000 € de marge nette — l'équivalent de nombreuses ventes.",
          },
          {
            name: 'Point de commande (réapprovisionnement)',
            formula: 'Point de commande = ventes journalières × délai de livraison + stock de sécurité',
            example: "Vous vendez 8 u./jour d'une famille, le fournisseur livre en 4 jours et vous gardez 2 jours de sécurité : point de commande = 8 × 4 + 8 × 2 = 48 u. Dans le jeu, enregistrez une consigne (seuil 48, quantité ≈ 30 jours soit 240 u.) : le réapro part tout seul avant la rupture.",
          },
          {
            name: "Délai d'amortissement d'un investissement",
            formula: 'Amortissement (jours) = coût de l\'investissement ÷ gain de CA quotidien',
            example: "Un réagencement à 2 400 € fait passer le CA moyen de 1 850 à 1 970 €/jour (+120 €). Amortissement = 2 400 ÷ 120 = 20 jours. Au-delà, tout est gagné.",
          },
        ],
      },
      {
        type: 'cases',
        title: 'Dans le jeu : situations retail & ROI',
        items: [
          {
            title: 'Les situations retail (tous les 5 à 8 jours)',
            situation: "Régulièrement, une situation concrète de terrain surgit : un fournisseur réclame une tête de gondole, la file d'attente déborde, le stock « s'évapore »... Trois choix vous sont proposés, chacun avec un coût et des effets temporaires sur le trafic, le panier ou la marge.",
            solution: "Reliez chaque situation au concept théorique du manuel (ISM, facing, zones chaudes, cross-merchandising, démarque, transformation, file d'attente, saisonnalité). Le bon choix n'est pas toujours le plus cher : négocier un facing (400 € + remise durable) bat souvent le chèque unique de 800 €.",
            lesson: "Chaque décision est consignée et analysée dans votre débrief pédagogique PDF : le formateur voit le concept, votre choix et la lecture experte associée.",
          },
          {
            title: "Lire le graphique Historique & ROI",
            situation: "Vous avez investi 2 400 € dans un réagencement. Comment prouver que l'investissement rapporte ?",
            solution: "L'onglet « Historique & ROI » trace le CA journalier de vos magasins ; chaque trait vertical marque la mise en service d'une amélioration. Le jeu compare la moyenne du CA sur les 7 jours AVANT l'achat à la moyenne des 7 jours APRÈS la fin des travaux, puis calcule le délai d'amortissement = coût ÷ gain quotidien.",
            lesson: "Un investissement ne se juge jamais sur un seul jour (bruit, week-ends, événements) mais sur une moyenne avant/après. C'est la démarche « test & learn » du retail réel.",
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 0b. EXPANSION GÉOGRAPHIQUE & RÉSEAU (SCD)
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'expansion',
    title: 'Expansion géographique',
    subtitle: 'Villes, domination locale, notoriété réseau',
    icon: 'Map',
    color: '#38BDF8',
    pages: [
      {
        type: 'cover',
        title: 'Expansion géographique',
        intro: "Ouvrir un second magasin ne consiste pas à dupliquer le premier : chaque ville a son bassin de population, ses loyers et ses événements. Développer un réseau, c'est arbitrer entre densifier une ville (domination locale) et couvrir le territoire (notoriété nationale), avant de franchir les frontières.",
        bullets: [
          '14 villes françaises + capitales européennes voisines',
          'Domination locale : varier les formats dans une même ville',
          'Notoriété réseau : des paliers de couverture à 3, 6 et 10 villes',
          'Événements locaux : chaque ville vit au rythme de son actualité',
        ],
      },
      {
        type: 'definitions',
        title: 'Concepts clés du développement',
        items: [
          { term: 'Bassin de chalandise (par ville)', def: "Population et pouvoir d'achat accessibles au magasin. Dans le jeu, chaque ville a un coefficient de bassin qui multiplie la demande : Paris 1,25, Nantes 1,00, Rennes 0,92. Un grand bassin ne suffit pas : il se paie en loyers (coefficient loyer ×1,8 à Paris contre ×0,85 à Rennes)." },
          { term: 'Coefficients de ville', def: "Trois curseurs par ville : bassin (demande), coef. loyer (loyer mensuel) et coef. coût (travaux d'ouverture). Une ville « douce » comme Rennes réduit le risque de démarrage ; une capitale maximise le potentiel mais exige une trésorerie solide." },
          { term: 'Formats de magasin', def: "Deux formats disponibles : la boutique et le magasin moyen (la grande surface spécialisée arrivera dans une prochaine version). Règle du jeu : maximum un magasin de chaque format par ville — pour grandir dans une ville déjà occupée, il faut varier les formats." },
          { term: 'Statuts de ville', def: "La présence dans une ville progresse selon le nombre de formats implantés : Présence (1 format) puis Domination (tous les formats). La Domination donne un bonus de trafic local de +5 % à tous les magasins de la ville." },
          { term: 'Domination locale', def: "Détenir tous les formats disponibles dans une même ville : le client vous croise partout, la marque devient un réflexe local. Bonus de +5 % de trafic sur place. C'est la stratégie de densification — l'inverse de l'éparpillement." },
          { term: 'Notoriété réseau (couverture)', def: "Être présent dans beaucoup de villes augmente la notoriété de l'enseigne partout : +2 % de trafic à partir de 3 villes, +4 % à 6 villes, +6 % à 10 villes. S'ajoute +1 % par capitale étrangère ouverte." },
          { term: "Déblocage de l'international", def: "Les capitales européennes voisines (Bruxelles, Berne, Madrid, Rome...) ne s'ouvrent qu'après une présence dans 3 villes françaises distinctes. Le jeu reproduit la réalité : on ne s'exporte pas avant d'avoir prouvé son modèle sur le marché domestique." },
          { term: 'Événements locaux', def: "Chaque ville connaît ses propres événements, positifs (Braderie de Lille +30 % de trafic, Fashion Week à Paris +20 %) ou négatifs (grève des transports −15 %, crue, tempête). Ils durent 3 à 6 jours et ne touchent que les magasins de la ville concernée : un réseau étalé lisse ces à-coups." },
          { term: 'Franchise (créer son réseau)', def: "Après 1 an d'exploitation et 3 magasins dans 2 villes, vous pouvez structurer votre concept en franchise (25 000 € : manuel opératoire, concept, juridique). Vous fixez le droit d'entrée (5 à 30 k€) et la redevance mensuelle (2 à 8 % du résultat du franchisé) : des conditions douces attirent beaucoup de candidats, des conditions dures en attirent peu mais rapportent plus par contrat. Les villes des franchisés comptent dans votre couverture réseau, jamais dans la Domination." },
          { term: "Financement d'une ouverture", def: "Le tunnel d'ouverture propose deux financements : paiement comptant (trésorerie) ou emprunt sur 48 mois. L'emprunt préserve le cash mais charge le compte de résultat en intérêts — le choix dépend de votre trésorerie et du délai d'amortissement attendu." },
        ],
      },
      {
        type: 'formulas',
        title: "Formules de l'expansion",
        items: [
          {
            name: 'Bonus de domination locale',
            formula: 'Bonus trafic ville = +5 % quand tous les formats sont présents',
            example: "À Lyon vous détenez la boutique ET le magasin moyen : tous vos magasins lyonnais gagnent +5 % de trafic.",
          },
          {
            name: 'Notoriété réseau',
            formula: 'Bonus trafic national = palier de couverture (+2/+4/+6 %) + 1 % × capitales étrangères',
            example: "Présent dans 6 villes dont Bruxelles : +4 % (palier 6 villes) + 1 % (capitale) = +5 % de trafic sur tout le réseau.",
          },
          {
            name: "Impact d'un événement local",
            formula: 'Trafic du jour = trafic normal × multiplicateur (0,85 à 1,30) pendant 3 à 6 jours',
            example: "Braderie de Lille (×1,3 pendant 3 j) sur un magasin à 1 800 €/j : ≈ +1 620 € de CA cumulé. Une grève (×0,85 pendant 5 j) coûte ≈ 1 350 €.",
          },
          {
            name: 'Revenus de franchise',
            formula: "Revenus = droit d'entrée (à la signature) + redevance mensuelle = % × résultat du franchisé",
            example: "Franchisé à Nantes, résultat 3 100 €/mois, redevance 5 % : 5 000 € encaissés à la signature puis ≈ 155 €/mois. Dix franchisés = un revenu passif qui finance vos propres ouvertures.",
          },
          {
            name: "Coût d'ouverture selon la ville",
            formula: 'Coût = devis de base × coef. coût ville ; Loyer = loyer de base × coef. loyer ville',
            example: "Une boutique dont le devis de base fait 20 000 € coûte ≈ 24 000 € à Paris (coef. 1,2) mais ≈ 17 000 € à Rennes (coef. 0,85), pour un loyer presque deux fois moindre.",
          },
        ],
      },
      {
        type: 'cases',
        title: 'Cas pratiques',
        items: [
          {
            title: 'Densifier ou couvrir ?',
            situation: "Vous avez 30 000 € de trésorerie. Deuxième format à Lyon (vers la domination) ou première boutique à Rennes (vers le palier 3 villes) ?",
            solution: "Les deux stratégies se valent mais ne rapportent pas pareil : le 2e format à Lyon déclenche la Domination locale (+5 % de trafic sur place), tandis que Rennes compte pour le palier de couverture (+2 % PARTOUT dès 3 villes). En début de partie, le bonus national touche peu de magasins : densifier d'abord, couvrir ensuite est souvent plus rentable.",
            lesson: "Dans le retail réel, c'est le débat « maillage dense » (Picard) contre « couverture nationale » (Fnac). Les deux marchent — l'erreur est de faire les deux à moitié.",
          },
          {
            title: "S'implanter à l'étranger trop tôt",
            situation: "L'international vient de se débloquer (3 villes françaises). Bruxelles vous tend les bras, mais votre trésorerie sort à peine d'un emprunt.",
            solution: "Une capitale cumule bassin fort ET coûts forts : loyer élevé, travaux chers, et le bonus capitale n'est que de +1 %. Si le magasin met 6 mois à s'amortir alors que votre trésorerie couvre 2 mois de charges, vous financez la conquête par la dette. Consolidez d'abord les marges du réseau français.",
            lesson: "L'expansion internationale se finance avec les profits du marché domestique, jamais avec sa trésorerie de survie.",
          },
        ],
      },
    ],
  },
  // ════════════════════════════════════════════════════════════════════
  // 0b2. FRANCHISE — DEVENIR FRANCHISEUR (SCD)
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'franchise',
    title: 'La franchise',
    subtitle: 'Devenir franchiseur : droits d\'entrée, redevances, animation réseau',
    icon: 'Network',
    color: '#10B981',
    pages: [
      {
        type: 'cover',
        title: 'La franchise',
        intro: "Après avoir prouvé votre concept en propre, vous pouvez le dupliquer sans investir un euro dans les murs : c'est la franchise. Des entrepreneurs indépendants exploitent votre enseigne, votre savoir-faire et vos méthodes — et vous versent un droit d'entrée puis des redevances. Dans le jeu, la franchise se débloque après 1 an d'exploitation avec au moins 3 magasins en propre répartis dans 2 villes.",
        bullets: [
          'Structurer son réseau : manuel opératoire, concept, cadre juridique (25 000 €)',
          "Fixer ses conditions : droit d'entrée (5 000 à 30 000 €) et redevance (2 à 8 %)",
          'Arbitrer attractivité contre rentabilité : conditions douces = candidatures fréquentes',
          'Animer son réseau : répondre aux demandes d\'aide sous peine de voir les franchisés partir',
        ],
      },
      {
        type: 'definitions',
        title: 'Les concepts clés de la franchise',
        items: [
          { term: 'Franchiseur', def: "L'entreprise qui a créé et éprouvé un concept commercial, puis le met à disposition d'indépendants contre rémunération. Dans le jeu, vous devenez franchiseur après 1 an d'exploitation, 3 magasins en propre et 2 villes couvertes — la preuve que le concept est duplicable." },
          { term: 'Franchisé', def: "Commerçant indépendant qui exploite l'enseigne du franchiseur à ses risques : il investit ses propres fonds, paie un droit d'entrée et reverse des redevances. Il n'est PAS un salarié : ses résultats dépendent de sa gestion locale — et de l'accompagnement du franchiseur." },
          { term: "Droit d'entrée", def: "Somme unique versée à la signature du contrat pour accéder à l'enseigne, à la formation initiale et au savoir-faire. Dans le jeu : 5 000 € (accessible), 15 000 € (standard) ou 30 000 € (exigeant). Un droit élevé finance vite votre structuration mais raréfie les candidatures." },
          { term: 'Redevance (royalties)', def: "Pourcentage prélevé chaque mois sur le résultat du franchisé : 2 %, 5 % ou 8 % dans le jeu. C'est le revenu récurrent du franchiseur — il grandit avec la performance du réseau, d'où l'intérêt d'aider chaque franchisé à progresser." },
          { term: 'Manuel opératoire', def: "Document qui codifie tout le savoir-faire de l'enseigne : agencement, assortiment, procédures, standards de service. Sa rédaction fait partie de la structuration du réseau (25 000 € dans le jeu, avec le concept et le cadre juridique). Sans manuel, pas de duplication fiable." },
          { term: 'Attractivité du réseau', def: "Plus vos conditions sont douces (5 000 € / 2 %), plus les candidatures affluent (environ une toutes les 2-3 semaines). Avec des conditions exigeantes (30 000 € / 8 %), comptez 6 à 8 semaines. Les candidatures expirent sous 6 jours : répondez vite depuis Développement → Franchise." },
          { term: 'Animation de réseau', def: "Le métier du franchiseur ne s'arrête pas à la signature : les franchisés demandent régulièrement de l'aide (animation commerciale). Financer (800 €, résultat +15 %), co-financer (300 €, +7 %) ou refuser (−8 %) — et ignorer la demande coûte −5 % et un franchisé refroidi." },
          { term: 'Résiliation', def: "Chaque mois, un franchisé peut mettre fin à son contrat (risque faible mais réel, accru par la déception). Vous perdez alors ses redevances ET la couverture de sa ville. Un réseau se retient par la valeur apportée, pas par le contrat." },
        ],
      },
      {
        type: 'formulas',
        title: 'Formules du franchiseur',
        items: [
          {
            name: 'Redevance mensuelle perçue par franchisé',
            formula: 'Redevance = résultat mensuel du franchisé × taux de redevance',
            example: "Un franchisé réalise 3 000 € de résultat mensuel avec une redevance de 5 % : vous percevez 150 €/mois. S'il progresse à 3 450 € (+15 % après une animation financée), la redevance passe à 172 €/mois — durablement.",
          },
          {
            name: 'Revenus totaux du réseau',
            formula: "Revenus franchise = Σ droits d'entrée + Σ redevances mensuelles",
            example: "4 franchisés signés à 15 000 € de droit d'entrée = 60 000 €, plus 4 × 150 € = 600 €/mois de redevances. Sur un an : 60 000 + 7 200 = 67 200 € de revenus sans immobiliser un euro dans les murs.",
          },
          {
            name: 'Retour sur structuration',
            formula: "Seuil = coût de structuration (25 000 €) ÷ droit d'entrée choisi",
            example: "Avec un droit d'entrée standard de 15 000 €, la structuration est remboursée dès le 2ᵉ franchisé signé. Avec un droit accessible de 5 000 €, il en faut 5 — mais ils arrivent trois fois plus vite.",
          },
          {
            name: "ROI d'une aide au franchisé",
            formula: 'Retour (mois) = coût de l\'aide ÷ gain de redevance mensuelle',
            example: "Financer une animation à 800 € pour un franchisé à 3 000 €/mois (redevance 8 %) : gain = 3 000 × 15 % × 8 % = 36 €/mois. Long à amortir seul… mais le refus coûte −8 % de résultat ET augmente le risque de départ : l'aide protège aussi le capital réseau.",
          },
        ],
      },
      {
        type: 'cases',
        title: 'Cas pratiques : franchiseur et franchisé',
        items: [
          {
            title: 'Cas nᵒ 1 — Le franchiseur qui anime son réseau',
            situation: "Votre enseigne de quincaillerie remplit les conditions (1 an, 3 magasins, 2 villes). Vous structurez le réseau (25 000 €), choisissez des conditions standard (15 000 € / 5 %) et signez 4 franchisés en 6 mois, à 3 000 € de résultat mensuel chacun. Deux d'entre eux demandent une animation commerciale.",
            solution: "Vous financez les deux animations (2 × 800 €). Leurs résultats passent à 3 450 €/mois. Bilan à 12 mois : 60 000 € de droits d'entrée + environ 7 600 € de redevances − 25 000 € de structuration − 1 600 € d'aides = +41 000 €. Et vos franchisés, satisfaits, restent : le réseau couvre 4 villes de plus sans un euro d'immobilier.",
            lesson: "La franchise est un levier de croissance « asset light » : le franchiseur gagne sur la durée si — et seulement si — il investit dans la réussite de ses franchisés.",
          },
          {
            title: 'Cas nᵒ 2 — Le réseau négligé',
            situation: "Même point de départ, mais vous choisissez des conditions exigeantes (30 000 € / 8 %) pour « rentabiliser vite ». Les candidatures n'arrivent que toutes les 6 à 8 semaines : 2 franchisés signés en 6 mois. Quand ils demandent de l'aide, vous refusez systématiquement pour économiser.",
            solution: "Chaque refus ampute leur résultat de 8 % (et une demande ignorée coûte −5 %). Un franchisé à 3 000 €/mois tombe sous 2 500 € après deux refus : vos redevances fondent (200 €/mois au lieu de 240 €) et sa déception nourrit le risque de résiliation mensuel. Au premier départ, vous perdez ses redevances ET la ville quitte votre couverture réseau. Bilan à 12 mois : 60 000 € de droits − 25 000 € de structuration ≈ +35 000 €, mais un réseau qui rétrécit au lieu de grandir.",
            lesson: "Des conditions dures rapportent plus par contrat mais freinent le recrutement ; un réseau qu'on n'anime pas se délite. Le vrai indicateur du franchiseur n'est pas le droit d'entrée encaissé, c'est la santé durable de ses franchisés.",
          },
        ],
      },
    ],
  },



  // ════════════════════════════════════════════════════════════════════
  // 0b3. DYNAMIQUE GROUPE — SIÈGE SOCIAL & FONCTIONS SUPPORT (SCD)
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'groupe',
    title: 'La dynamique groupe',
    subtitle: 'Siège social, fonctions support et investissements groupe',
    icon: 'Building2',
    color: '#F59E0B',
    pages: [
      {
        type: 'cover',
        title: 'La dynamique groupe',
        intro: "À 3 magasins en propre, votre enseigne change de dimension : elle devient un groupe. Gérer chaque boutique à la main ne suffit plus — il faut un siège social, des fonctions support (achats, RH, finance, marketing…) et des investissements structurants. C'est la bascule du commerçant vers le dirigeant d'entreprise.",
        bullets: [
          'Déblocage automatique à 3 magasins en propre (onglet Groupe)',
          'Le siège social (18 000 € + 2 500 €/mois) est le passage obligé avant tout recrutement',
          'Chaque fonction support a un effet mesurable : CA, prix d\'achat, masse salariale, retards…',
          '4 investissements groupe : centrale d\'achats, ERP, plateforme logistique, académie',
        ],
      },
      {
        type: 'definitions',
        title: 'Les concepts clés du groupe',
        items: [
          { term: 'Dynamique groupe', def: "Passage d'une collection de magasins à une organisation structurée : à 3 points de vente en propre, les fonctions transverses (acheter, recruter, financer, communiquer) deviennent trop lourdes pour le dirigeant seul. Dans le jeu, l'onglet Groupe se déverrouille à ce seuil." },
          { term: 'Siège social', def: "Le bureau central du groupe : 18 000 € d'aménagement + 2 500 €/mois de loyer. C'est la porte d'entrée obligatoire — impossible de recruter une fonction support sans siège. Dans la vraie vie comme dans le jeu, un réseau sans tête de pont ne tient pas." },
          { term: 'Fonction support', def: "Un cadre du siège qui travaille pour TOUS les magasins à la fois : directeur de réseau, acheteur, DRH, DAF, responsable marketing… Son coût est fixe (salaire mensuel), son effet est proportionnel à la taille du réseau — d'où l'intérêt de recruter quand on grandit." },
          { term: 'Centrale d\'achats', def: "Investissement groupe (25 000 € + 900 €/mois) qui massifie les commandes de tous les magasins : −3 % sur tous les prix d'achat fournisseurs. Se cumule avec les acheteurs du siège (jusqu'à −12 % au total)." },
          { term: 'ERP / DSI', def: "Le système d'information du groupe (15 000 € + 600 €/mois) : stocks, caisses et indicateurs unifiés en temps réel → +5 % de CA réseau. Un groupe qui pilote à vue perd des ventes sans le savoir." },
          { term: 'Plateforme logistique', def: "Entrepôt central (30 000 € + 1 100 €/mois, nécessite la centrale d'achats) qui consolide les livraisons fournisseurs : −50 % de risque de retard. Se cumule avec les coordinateurs supply chain du siège." },
          { term: 'Académie de formation', def: "Centre de formation interne (12 000 € + 500 €/mois) : des équipes mieux formées, des clients mieux servis → +3 points de satisfaction clients par mois." },
          { term: 'Masse salariale', def: "La somme des salaires chargés de vos équipes magasin. Une DRH (−5 %) et un chasseur de têtes (−3 %) l'optimisent : contrats mieux calibrés, moins de turnover, recrutements plus justes." },
        ],
      },
      {
        type: 'formulas',
        title: 'Les arbitrages du dirigeant de groupe',
        items: [
          {
            name: 'Seuil de rentabilité d\'une fonction support',
            formula: 'CA réseau minimal = coût mensuel de la recrue ÷ (% de gain × marge)',
            example: "Un directeur de réseau à 4 260 €/mois apporte +10 % de CA. Avec 40 % de marge, il s'autofinance dès que le réseau dépasse ~107 000 € de CA mensuel (4 260 ÷ (0,10 × 0,40)). Recruté trop tôt, il coûte ; recruté au bon moment, il rapporte.",
          },
          {
            name: 'Gain de la centrale d\'achats',
            formula: 'Économie mensuelle = achats mensuels du réseau × remise cumulée',
            example: "Un réseau qui commande 30 000 €/mois de marchandises avec acheteur (−2 %), responsable achats (−3 %) et centrale (−3 %) économise 2 400 €/mois — la centrale (900 €/mois) et les salaires sont vite couverts.",
          },
          {
            name: 'Coût complet du siège',
            formula: 'Charge mensuelle groupe = loyer siège + salaires support + charges investissements',
            example: "Siège (2 500 €) + 3 recrues (~9 000 €) + ERP et centrale (1 500 €) = ~13 000 €/mois de frais de structure. Un groupe se construit sur un réseau déjà rentable, jamais pour sauver un réseau qui perd de l'argent.",
          },
        ],
      },
      {
        type: 'cases',
        title: 'Cas pratique : structurer au bon moment',
        items: [
          {
            title: 'Cas nᵒ 1 — Le groupe structuré',
            situation: "Votre 3ᵉ magasin ouvre, le réseau génère 90 000 € de CA mensuel rentable. Vous inaugurez le siège (18 000 €), recrutez un acheteur (2 600 €/mois, −2 % achats) et une assistante de direction (2 414 €/mois, +4 % CA), puis la centrale d'achats quand le 4ᵉ magasin ouvre.",
            solution: "L'acheteur économise ~540 €/mois sur 27 000 € d'achats, l'assistante ajoute ~3 600 € de CA (+1 440 € de marge). Le coût de structure (7 500 €/mois avec le loyer) est absorbé et chaque nouveau magasin profite des mêmes fonctions sans coût additionnel : c'est l'effet d'échelle.",
            lesson: "Les fonctions support sont un coût fixe qui se dilue dans la croissance : plus le réseau grandit, plus chaque recrue du siège rapporte.",
          },
          {
            title: 'Cas nᵒ 2 — Le siège prématuré',
            situation: "Dès le 3ᵉ magasin ouvert (réseau à peine à l'équilibre, 35 000 € de CA mensuel), vous inaugurez le siège et recrutez d'un coup directeur de réseau, DRH et DAF : 12 760 €/mois de salaires + 2 500 € de loyer.",
            solution: "Les bonus (+10 % CA = 3 500 €, −5 % masse salariale ≈ 600 €) rapportent ~2 000 €/mois de marge quand la structure en coûte 15 260. La trésorerie fond de ~13 000 €/mois : le siège devient le fossoyeur du réseau qu'il devait servir.",
            lesson: "Le bon ordre : réseau rentable → siège → recrues dont le seuil de rentabilité est déjà atteint → investissements groupe. La structure suit la croissance, elle ne la précède pas.",
          },
        ],
      },
    ],
  },


  // ════════════════════════════════════════════════════════════════════
  // 0b'. MERCHANDISING : FACING & PARCOURS CLIENT (SCD)
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'merchandising',
    title: 'Merchandising',
    subtitle: 'Planogramme, facing, parcours client et zones chaudes',
    icon: 'LayoutGrid',
    color: '#0EA5E9',
    pages: [
      {
        type: 'cover',
        title: 'Merchandising : ce que le client voit et ce qu\'il traverse',
        intro: "Le merchandising est l'art de vendre sans vendeur : la place d'un produit sur l'étagère et le chemin du client dans le magasin décident d'une part énorme des achats. Dans SCD Game, la page Merchandising propose deux ateliers interactifs — le planogramme (facing par niveau de rayon) et le parcours client (plan du magasin en zones chaudes/froides). Chacun est noté sur 100 et agit chaque jour sur vos ventes.",
        bullets: [
          'Planogramme : 4 niveaux de rayon × 4 à 10 facings selon le format du magasin',
          'Parcours client : grille 3 × 4, entrée et caisses fixes, chaleur de 40 à 100 % par case',
          'Première mise en place offerte, puis manutention 150 € (facing) / réagencement 400 € (parcours) × coefficient de format',
          'Effets quotidiens : ventes par famille, conversion, panier moyen, trafic, satisfaction',
        ],
      },
      {
        type: 'definitions',
        title: 'Les concepts clés',
        items: [
          { term: 'Planogramme', def: "Schéma d'implantation d'un rayon : quelle famille occupe quel emplacement, sur combien de facings. Dans le jeu, chaque case de la grille est un facing ; la visibilité d'un facing dépend de son niveau : yeux ×1,0, mains ×0,85, haut ×0,7, bas ×0,6." },
          { term: 'Facing', def: "Nombre de produits visibles de face. La règle d'or : la part de visibilité d'une famille doit être proportionnelle à sa part de demande. Une famille sans facing n'est pas vue : ses ventes chutent de 20 % (×0,8). Sous-exposée (ratio < 0,7) elle perd jusqu'à 10 % ; sur-exposée (ratio > 1,4) elle gagne au mieux 8 % — et gaspille du linéaire." },
          { term: 'Niveau des yeux', def: "L'étagère reine (1,50-1,70 m). Le jeu mesure la part de ce niveau donnée aux familles à fort coefficient (marge ≥ médiane du secteur) : chaque 10 % de cette étagère confiés aux familles rentables ajoute +0,3 % de panier moyen (jusqu'à +3 %)." },
          { term: 'Facing promo', def: "Un emplacement « étiquette rouge » dans le rayon : +1 point de conversion chacun, plafonné à 2. Au-delà, la remise cannibalise les ventes à plein prix sans gain supplémentaire." },
          { term: 'Linéaire vide', def: "Une étagère à trous donne une impression de rupture et de magasin négligé : chaque 10 % d'emplacements vides coûte 1 point de conversion." },
          { term: 'Zones chaudes / froides', def: "La chaleur d'une case est son passage naturel : 100 % devant l'entrée, 85-95 % près des caisses, 40-45 % au fond. Les familles placées en zone chaude vendent un peu plus (×1,04 à 100 %), celles en zone froide un peu moins (×0,97 à 40 %)." },
          { term: 'Produit d\'appel', def: "La famille la plus demandée de votre secteur. Placée au fond (chaleur ≤ 50 %), elle force le client à traverser le magasin : +3 points de conversion, +1 % de trafic, et toutes les cases gagnent +10 % de chaleur (+15 % pour ses voisines). Placée à l'entrée (≥ 85 %), le client la prend, paie et repart : −2 points de conversion." },
          { term: 'Cross-merchandising', def: "Deux familles complémentaires (définies par secteur : outillage + visserie, rouges + spiritueux, femme + accessoires…) placées côte à côte déclenchent l'achat complémentaire : +1,5 % de panier moyen par paire, jusqu'à 3 paires." },
          { term: 'Saisonnalité du rayon', def: "Le score du planogramme est calculé sur la demande du mois en cours : chaque famille a son coefficient saisonnier (le jardin au printemps, les effervescents en décembre…). Un rayon parfait en janvier peut être déséquilibré en novembre. Le 1er du mois, un message annonce les familles qui montent ou descendent le mois suivant et le score prévisionnel ; la suggestion automatique tient compte de la saison." },
          { term: 'Conception par secteur', def: "Le planogramme et le parcours se conçoivent une fois par secteur d'activité : tous les magasins du même secteur suivent la même organisation. Ouvrir un magasin dans un nouveau secteur impose de refaire l'exercice pour ce secteur — tant que son rayon n'est pas posé, ses magasins subissent −3 % de ventes. La fiche PDF « réserve » imprime planogramme et plan du magasin pour l'équipe." },
          { term: 'Tête de gondole négociée', def: "Les 1er et 15 du mois, un fournisseur avec lequel la relation est correcte (≥ 40) peut proposer de payer 2 ou 3 facings au niveau des yeux pour l'une de ses familles — souvent une famille à faible marge — pendant 60 jours, contre une coopération commerciale encaissée chaque jour (environ 110 € par facing et par mois selon le format). L'arbitrage est affiché avant de signer : score du planogramme, marge aux yeux et ventes de la famille avant/après. Les emplacements sont verrouillés jusqu'à la fin du contrat ; accepter améliore la relation fournisseur (+5)." },
          { term: 'Zones de service', def: "Produits d'impulsion contre la file de caisse : +2 % de panier (+0,5 % ailleurs). Zone promo à l'entrée : +3 % de trafic mais −1 % de panier (ailleurs +1 % de trafic). Comptoir conseil / SAV : +1 point de conversion et satisfaction en hausse." },
        ],
      },
      {
        type: 'formulas',
        title: 'Les règles de score',
        items: [
          {
            name: 'Score planogramme (/100)',
            formula: '50 × (1 − écart moyen visibilité/demande) + 25 × part du niveau des yeux aux familles à forte marge + 25 × (1 − part d\'emplacements vides)',
            example: "Écart moyen de 10 %, 100 % de l'étagère des yeux aux familles rentables, aucun trou : 45 + 25 + 25 = 95/100. Une étagère remplie d'une seule famille : écart ≈ 80 %, score ≈ 35.",
          },
          {
            name: 'Effet ventes d\'une famille (planogramme)',
            formula: 'multiplicateur = 1 + (ratio visibilité/demande − 1) × 0,15, borné entre 0,90 et 1,08 ; 0 facing = 0,80',
            example: "Une famille qui pèse 30 % de la demande mais 36 % de la visibilité : ratio 1,2 → ×1,03. Une famille à 24 % de demande et 16 % de visibilité : ratio 0,67 → ×0,95.",
          },
          {
            name: 'Score parcours (/100)',
            formula: '40 de base + 25 (produit d\'appel au fond, 10 en zone tiède, 0 à l\'entrée) + 10 impulsion en caisse (5 ailleurs) + 10 promo à l\'entrée (5 ailleurs) + 5 comptoir SAV + 5 par paire complémentaire (max 10) − 5 par zone vide − 5 par famille absente',
            example: "Produit d'appel au fond, impulsion en caisse, promo à l'entrée, SAV, 2 paires, aucune case vide : 40 + 25 + 10 + 10 + 5 + 10 = 100.",
          },
          {
            name: 'Effets globaux du parcours',
            formula: 'Trafic = +1 (appel au fond) +3 (promo entrée) ; Conversion = +3 (appel au fond) −2 (appel entrée) +1 (SAV) −1/zone vide ; Panier = +2 (impulsion caisse) +1,5/paire −1 (promo entrée)',
            example: "Plafonds : trafic +4 %, conversion +5 points, panier +6 %. Ces effets s'ajoutent à ceux de l'équipe, du marketing et des améliorations du magasin.",
          },
        ],
      },
      {
        type: 'cases',
        title: 'Cas pratiques : deux magasins, deux rayons',
        items: [
          {
            title: 'Cas nᵒ 1 — Le planogramme « tout pour la star »',
            situation: "Dans votre quincaillerie, la visserie représente 30 % de la demande. Convaincu que c'est elle qui fait venir les clients, vous lui donnez 14 facings sur 24, dont toute l'étagère des yeux ; l'électricité et la peinture se partagent le niveau bas et le jardin disparaît du rayon.",
            solution: "Visserie : ratio ≈ 2 → ×1,08 (plafond) mais 8 facings gaspillés. Jardin : 0 facing → ×0,80. Électricité/peinture au niveau bas : visibilité ×0,6, ratio ≈ 0,5 → ×0,92. L'étagère des yeux est occupée par la famille au plus faible coefficient : part « marge aux yeux » = 0 → aucun bonus panier. Score ≈ 35/100. En rééquilibrant (facings ∝ demande, outillage et peinture — coefficients élevés — aux yeux), le bouton Suggestion atteint 93/100 et +3 % de panier.",
            lesson: "Le facing se répartit au prorata de la demande ; l'étagère des yeux se réserve à la marge, pas au volume. Sur-exposer une famille ne crée pas de demande, cela en cache d'autres.",
          },
          {
            title: 'Cas nᵒ 2 — Le parcours « tout à portée de main »',
            situation: "Votre caviste place les rouges (produit d'appel, 35 % de la demande) juste devant l'entrée « pour que les clients trouvent tout de suite », la zone promo au fond « pour faire découvrir », les bières isolées dans un angle et deux cases restent vides.",
            solution: "Rouges à l'entrée : −2 points de conversion, aucune case réchauffée. Promo au fond : +1 % de trafic seulement. Deux zones vides : −2 points de conversion et −10 au score. Aucune paire complémentaire. Score ≈ 40 − 10 + 5 = 35/100, conversion nette −4 points. En inversant (rouges au fond, promo à l'entrée, spiritueux à côté des rouges, bières près des spiritueux, impulsion en file de caisse, SAV), le score monte à 100 : +4 % trafic, +4 points conversion, +5,5 % panier.",
            lesson: "Le client doit traverser le magasin pour atteindre ce qu'il est venu chercher : chaque mètre parcouru expose de l'offre. Les zones chaudes se méritent avec l'impulsion et la promo, les zones froides se réchauffent avec le produit d'appel.",
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 0c. CONCURRENCE & GESTION DU PARC (SCD)
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'parc',
    title: 'Concurrence & gestion du parc',
    subtitle: 'Concurrents, murs, cession, liquidation',
    icon: 'Building2',
    color: '#F59E0B',
    pages: [
      {
        type: 'cover',
        title: 'Concurrence & gestion du parc',
        intro: "Un réseau de magasins vit : des concurrents s'installent, des acheteurs font des offres, des baux se négocient. Gérer son parc, c'est savoir attaquer (occuper le terrain avant les autres), défendre (répondre à une guerre des prix) et arbitrer (acheter ses murs, céder un fonds, liquider un point de vente).",
        bullets: [
          "Trois enseignes rivales s'étendent sur la carte en même temps que vous",
          'Concurrence locale : un malus de trafic durable',
          'Acheter ses murs : devenir propriétaire plutôt que locataire',
          'Sortir proprement : cession du fonds, liquidation ou fermeture',
        ],
      },
      {
        type: 'definitions',
        title: 'La concurrence sur la carte',
        items: [
          { term: 'Enseignes concurrentes', def: "Trois rivaux (Distrimax, HyperNord, Galleria) ouvrent un magasin toutes les 2 à 3 semaines environ. Trois fois sur quatre ils visent une ville libre — dans un cas sur quatre, ils s'installent en face de chez vous. Une ville prise reste pénalisée même si vous vous y implantez plus tard." },
          { term: 'Malus de concurrence locale', def: "Un concurrent dans votre ville coûte −8 % de trafic à vos magasins sur place, et −12 % s'il est installé depuis plus de 30 jours (il a fidélisé sa clientèle). D'où l'urgence d'occuper les meilleures villes avant les rivaux." },
          { term: 'Guerre des prix', def: "Le concurrent casse les prix. Trois réponses : s'aligner (surcoût de 1,5 % du CA pendant 10 jours — on protège le trafic en sacrifiant la marge), lancer une campagne locale à 600 € (+4 % de trafic 10 jours — on défend par la différenciation), ou ne rien faire (−7 % de trafic supplémentaire pendant 10 jours)." },
          { term: 'Rachat opportuniste', def: "Un concurrent local en difficulté propose sa boutique (14 000 à 24 000 €). Racheter au prix affiché est sûr ; négocier −30 % ne réussit qu'une fois sur deux (échec = l'occasion s'envole). Le rachat supprime le malus de concurrence ET ajoute un magasin déjà installé à votre réseau." },
          { term: 'Fonds de commerce', def: "Ce que vous vendez quand vous cédez un magasin : la clientèle, l'emplacement (droit au bail), le matériel et l'enseigne — pas les murs. Sa valeur dépend du loyer, de la surface et des aménagements réalisés." },
          { term: 'Les murs (immobilier commercial)', def: "Le local lui-même. Les acheter transforme un loyer versé à fonds perdus en patrimoine : plus de loyer à payer, un actif qui s'apprécie (+2 %/an dans le jeu) et zéro indemnité de bail en cas de fermeture. Prix : environ 11 ans de loyer." },
          { term: 'Hypothèque', def: "Crédit immobilier adossé aux murs : 15 ans à 4,2 % dans le jeu. La mensualité remplace le loyer — si elle est proche du loyer économisé, l'opération s'autofinance et vous construisez un patrimoine « gratuitement »." },
          { term: 'Bien locatif', def: "Si vous vendez le fonds en gardant les murs (ou fermez un magasin dont vous possédez les murs), le local devient un bien locatif : vous encaissez le loyer du repreneur chaque mois. C'est la rente du commerçant devenu propriétaire." },
          { term: 'Cession du fonds', def: "Vous fixez un prix demandé ; des offres arrivent (85 à 107 % de l'estimation), valables 3 jours. Plus votre prix est raisonnable, plus les offres sont fréquentes : à moins de 90 % de l'estimation, comptez une offre tous les 7 jours ; à plus de 140 %, quasiment aucune. Une seule contre-offre possible — l'acheteur remonte parfois à mi-chemin, sinon il se retire." },
          { term: 'Liquidation', def: "7 jours de vente spéciale à −40 % pour écouler le stock, puis fermeture sans indemnité de bail. On récupère une partie de la valeur du stock, mais rien du fonds. À réserver aux magasins invendables." },
          { term: 'Fermeture sèche & indemnité de bail', def: "Fermer du jour au lendemain coûte 3 mois de loyer d'indemnité au bailleur (rupture anticipée du bail) — sauf si vous possédez les murs. La sortie la moins chère est presque toujours la cession, puis la liquidation, et en dernier recours la fermeture sèche." },
        ],
      },
      {
        type: 'formulas',
        title: 'Formules du parc',
        items: [
          {
            name: 'Estimation du fonds de commerce',
            formula: 'Estimation ≈ loyer mensuel × 8 + surface (m²) × 45 € + 800 € × niveaux d\'amélioration',
            example: "Boutique de 90 m², loyer 1 100 €, 3 niveaux d'améliorations : 8 800 + 4 050 + 2 400 = 15 250 €. Fixez le prix demandé autour de cette estimation pour recevoir des offres.",
          },
          {
            name: 'Prix et rentabilité des murs',
            formula: 'Prix des murs = 132 × loyer mensuel (≈ 11 ans de loyer) ; valeur ensuite : +2 %/an',
            example: "Loyer 1 000 €/mois → murs à 132 000 €. Payés cash, ils s'amortissent en 11 ans d'économie de loyer, pendant que leur valeur de revente grimpe.",
          },
          {
            name: "Mensualité d'hypothèque (15 ans, 4,2 %)",
            formula: 'Mensualité = capital × (t ÷ 12) ÷ (1 − (1 + t ÷ 12)⁻¹⁸⁰)',
            example: "Murs à 132 000 € : mensualité ≈ 990 €. Si le loyer économisé était de 1 000 €, l'opération s'autofinance : même sortie de cash mensuelle, mais vous devenez propriétaire.",
          },
          {
            name: "Coût de sortie d'un magasin",
            formula: 'Cession : + prix du fonds (+ 97 % des murs si vendus) · Liquidation : 7 j à −40 % puis 0 € · Fermeture sèche : − 3 mois de loyer',
            example: "Magasin au loyer de 1 200 € : la fermeture sèche coûte 3 600 €. La liquidation rapporte une partie du stock. La cession à 15 000 € rapporte 15 000 €. L'ordre de préférence est presque toujours le même.",
          },
        ],
      },
      {
        type: 'cases',
        title: 'Cas pratiques',
        items: [
          {
            title: 'HyperNord ouvre en face de chez vous',
            situation: "Un concurrent s'installe dans votre ville : −8 % de trafic immédiat, −12 % s'il s'enracine 30 jours. Une guerre des prix menace.",
            solution: "Ne subissez pas : la campagne locale (600 €, +4 % de trafic) est souvent le meilleur ratio coût/effet face à un alignement qui sacrifie 1,5 % de CA. Et si le concurrent propose son fonds plus tard, le rachat supprime définitivement le malus tout en agrandissant votre réseau.",
            lesson: "Face à un nouvel entrant, on défend sa clientèle par la différenciation (service, animation locale) avant de dégrader sa marge par les prix.",
          },
          {
            title: 'Acheter ses murs : cash ou hypothèque ?',
            situation: "Vos murs valent 132 000 € (loyer 1 000 €/mois). Vous avez 150 000 € de trésorerie.",
            solution: "Payer cash économise les intérêts mais vide la trésorerie — dangereux si un imprévu survient. L'hypothèque 15 ans à 4,2 % donne une mensualité (≈ 990 €) proche du loyer supprimé : effort de trésorerie quasi nul, cash préservé pour l'expansion, et l'actif s'apprécie de 2 %/an.",
            lesson: "En immobilier commercial, l'emprunt n'est pas un aveu de faiblesse : c'est un levier. On garde le cash pour ce que la banque ne finance pas.",
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 1. FISCALITÉ & STATUTS JURIDIQUES
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'fiscalite',
    title: 'Fiscalité & Statuts juridiques',
    subtitle: 'TVA, IS, statuts progressifs',
    icon: 'Scale',
    color: '#7C3AED',
    pages: [
      {
        type: 'cover',
        title: 'Fiscalité & Statuts juridiques',
        intro: "La fiscalité encadre la vie de votre entreprise dès le premier euro de chiffre d'affaires. Connaître les seuils et les obligations vous permet d'anticiper plutôt que de subir.",
        bullets: [
          'Auto-entrepreneur → EURL → SARL → SAS selon le CA',
          'TVA : franchise, simplifié, normal',
          'Charges sociales TNS vs salarié',
        ],
      },
      {
        type: 'definitions',
        title: 'Définitions essentielles',
        items: [
          { term: 'Auto-entrepreneur', def: "Statut individuel simplifié. Franchise de TVA jusqu'à 91 900 € de CA. Charges sociales forfaitaires (~22 % du CA)." },
          { term: 'EURL', def: 'Entreprise Unipersonnelle à Responsabilité Limitée. Patrimoine pro/perso séparés. Gérant TNS, charges ~45 % de la rémunération.' },
          { term: 'SARL', def: 'Société à Responsabilité Limitée. 2 associés minimum. Responsabilité limitée aux apports. Souple, adaptée aux PME familiales.' },
          { term: 'SAS', def: "Société par Actions Simplifiée. Président assimilé salarié (~75 % de charges). Statuts ultra-flexibles, idéale pour lever des fonds." },
          { term: 'TVA collectée', def: 'Taxe que vous facturez à vos clients (20 % en France pour le transport intra-EU).' },
          { term: 'TVA déductible', def: 'Taxe que vous payez sur vos achats (carburant, véhicules, fournitures) et que vous récupérez auprès du Trésor.' },
          { term: 'Régime simplifié', def: "Déclaration TVA semestrielle. Disponible jusqu'à 840 000 € de CA HT." },
          { term: 'Régime normal', def: 'Déclaration TVA mensuelle. Obligatoire au-delà de 840 000 € de CA HT.' },
          { term: 'Provisionnement fiscal automatique (Midal)', def: "Mécanisme cantonnant chaque jour la TVA collectée nette et 25 % du bénéfice du jour sur deux réserves dédiées (TVA, IS). Disponible uniquement après recrutement de Tristan Midal (DAF). Le sur-provisionnement éventuel est reversé en trésorerie à la clôture annuelle." },
          { term: 'Réserve fiscale 25 % (Livret A)', def: "Option facultative : à chaque clôture mensuelle, 25 % du bénéfice net est transféré sur un Livret A pro (3 % APY, intérêts capitalisés quotidiennement). Libéré automatiquement à la clôture annuelle (top-up IS si déficit, sinon retour trésorerie)." },
        ],
      },
      {
        type: 'formulas',
        title: 'Formules de calcul',
        items: [
          {
            name: 'TVA à reverser',
            formula: 'TVA due = TVA collectée − TVA déductible',
            example: "Vous facturez 50 000 € HT (TVA 10 000 €), achetez du carburant pour 8 000 € HT (TVA 1 600 €). TVA due = 10 000 − 1 600 = 8 400 €.",
          },
          {
            name: 'Conversion HT / TTC',
            formula: 'Prix TTC = Prix HT × (1 + taux TVA)\nPrix HT = Prix TTC ÷ (1 + taux TVA)',
            example: 'Contrat affiché 12 000 € TTC à 20 % de TVA → HT = 12 000 / 1,20 = 10 000 €.',
          },
          {
            name: 'Charges sociales TNS (gérant majoritaire)',
            formula: 'Charges TNS ≈ 45 % de la rémunération nette',
            example: "Vous vous versez 3 000 €/mois nets → ~1 350 € de charges supplémentaires (URSSAF).",
          },
          {
            name: 'Impôt sur les sociétés (IS) — taux simplifié',
            formula: 'IS = 15 % × bénéfice jusqu\'à 42 500 € + 25 % au-delà',
            example: 'Bénéfice 60 000 € → IS = 15 % × 42 500 + 25 % × 17 500 = 6 375 + 4 375 = 10 750 €.',
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Vocabulaire',
        items: [
          { word: 'BIC', def: 'Bénéfices Industriels et Commerciaux — catégorie fiscale du transport routier.' },
          { word: 'Capital social', def: "Apport initial des associés. Minimum légal SARL/SAS : 1 €. En pratique : viser ≥ 5 000 € pour crédibilité bancaire." },
          { word: 'Dividende', def: "Part du bénéfice distribuée aux associés. Soumis à la flat tax (30 %) ou barème progressif." },
          { word: 'Exercice fiscal', def: "Période de 12 mois pour laquelle on calcule le résultat. Souvent calé sur l'année civile." },
          { word: 'Liasse fiscale', def: 'Document annuel envoyé aux impôts résumant bilan, compte de résultat, annexes.' },
          { word: 'URSSAF', def: 'Organisme collecteur des cotisations sociales (sécurité sociale, retraite, allocations familiales).' },
        ],
      },
      {
        type: 'diagram',
        title: 'Choisir son statut juridique',
        intro: "La progression des statuts suit le chiffre d'affaires. Anticipez le changement pour éviter une bascule fiscale brutale.",
        diagram: 'pyramid-statuts',
        legend: [
          { label: 'Auto-entrepreneur', desc: 'Démarrage simple, mais plafond bas et pas de TVA récupérable.' },
          { label: 'EURL',              desc: 'Patrimoine protégé, charges sociales TNS (~45 %).' },
          { label: 'SARL',              desc: 'Plusieurs associés, gestion souple, cadre éprouvé.' },
          { label: 'SAS',               desc: 'Idéale pour lever des fonds, président assimilé salarié (~75 % de charges).' },
        ],
      },
      {
        type: 'cases',
        title: 'Cas pratiques',
        items: [
          {
            title: "Passage du seuil franchise TVA",
            situation: "Vous êtes auto-entrepreneur. Votre CA cumulé sur les 12 derniers mois atteint 89 000 € et vous prévoyez 8 000 € ce mois-ci.",
            solution: "Vous dépasserez 91 900 € — vous sortez de la franchise. Dès le 1er jour du mois de dépassement, vous devez facturer 20 % de TVA. Anticipez : ajustez vos prix HT, mettez à jour vos modèles de facture, et déclarez votre première TVA le mois suivant.",
            lesson: 'En jeu, surveillez l\'indicateur "CA 12 mois glissant" sur la page Fiscalité. Le passage en TVA réduit la rentabilité apparente de 17 %.',
          },
          {
            title: 'Choix EURL vs SASU à 100 k€ de CA',
            situation: "Vous générez 100 k€/an. Vous hésitez entre EURL (gérant TNS, ~45 % de charges) et SASU (président assimilé salarié, ~75 % de charges).",
            solution: "À volume égal de rémunération, l'EURL est ~30 % moins chère en charges sociales. La SASU est néanmoins préférable si vous voulez vous verser des dividendes (flat tax 30 %, sans cotisations sociales) ou ouvrir le capital plus tard.",
            lesson: 'Choisissez en fonction de votre stratégie de sortie : EURL pour optimiser la rému courante, SASU pour la fiscalité des dividendes et la flexibilité.',
          },
          {
            title: 'Régime simplifié → régime normal',
            situation: "Votre CA HT vient de franchir 840 000 €. Votre comptable vous appelle.",
            solution: "Vous basculez au régime réel normal : déclaration TVA MENSUELLE (au lieu de semestrielle). Mettez en place un processus de clôture mensuelle dès le mois suivant, sous peine de retards et de pénalités URSSAF.",
            lesson: 'Le passage en régime normal ne change pas l\'impôt dû — il change la fréquence et la rigueur de la déclaration. Bug ou retard = 0,4 %/mois de pénalité.',
          },
          {
            title: 'Provisionnement automatique TVA + IS (perk Midal)',
            situation: "Vous venez de recruter Tristan Midal (DAF) et activez le toggle « Provisionnement fiscal automatique » dans Fiscalité. Bénéfice journalier : ~600 €. TVA collectée nette : ~120 €/jour.",
            solution: "Chaque jour ouvré, le moteur débite automatiquement de votre trésorerie : 120 € → réserve TVA + 25 % × 600 € = 150 € → réserve IS. Votre trésorerie affichée correspond DÉSORMAIS à votre vrai cash disponible. À la déclaration TVA / aux acomptes IS, la réserve paie en priorité. En clôture annuelle, tout sur-provisionnement (si l'IS réel est < 25 %) est reversé en bonus dans la trésorerie.",
            lesson: "Le toggle Midal élimine la mauvaise surprise du « gros chèque IS de fin d'année ». Coût : zéro — c'est juste un cantonnement comptable. À activer DÈS que Midal est recruté, sauf si vous savez gérer la rigueur d'auto-provisionnement vous-même.",
          },
          {
            title: 'Réserve fiscale 25 % sur Livret A (3 % APY)',
            situation: "Vous activez l'option « Réserve fiscale » (Fiscalité → Aperçu). Bénéfice net du mois : 8 000 €.",
            solution: "À la clôture mensuelle, 25 % du bénéfice net (= 2 000 €) est automatiquement transféré sur un compte Livret A séparé, rémunéré 3 %/an (capitalisation quotidienne). À la clôture annuelle, la totalité est libérée : couvre l'IS si manque, sinon retour direct en trésorerie + les intérêts capitalisés (~30-60 € sur 1 an pour 24k de réserve moyenne).",
            lesson: "Cumul possible avec le toggle Midal : Midal provisionne au rythme d'1/365e par jour, la réserve Livret A cumule 25 % du résultat mensuel. Les deux ensemble = vrai filet de sécurité fiscal + petit gain d'intérêts. À désactiver si vous avez besoin du cash pour investir maintenant.",
          },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Pièges à éviter',
        items: [
          {
            title: 'Confondre TVA collectée et trésorerie',
            mistake: "Vous voyez 10 000 € de TVA collectée sur votre compte et vous l'utilisez pour payer un salaire.",
            signal: 'Solde de trésorerie ≥ TVA due affichée en orange sur le tableau de bord Fiscalité.',
            fix: "La TVA n'est PAS votre argent — vous la collectez pour le compte de l'État. Réservez-la mensuellement sur un compte séparé ou un sous-compte « TVA à reverser ».",
          },
          {
            title: 'Rester auto-entrepreneur après 92 k€ de CA',
            mistake: "Vous dépassez le plafond mais continuez à facturer sans TVA pour éviter la paperasse.",
            signal: 'Notification rose « Plafond franchise TVA dépassé » + amende fiscale en cas de contrôle.',
            fix: 'Basculez en EURL/SASU dès que le CA prévisionnel sur 12 mois atteint 85 k€. Vous gagnez la TVA déductible (carburant, véhicules) qui compensera largement les nouvelles cotisations.',
          },
          {
            title: 'Oublier la CFE',
            mistake: "Vous payez l'IS et la TVA mais ignorez la Cotisation Foncière des Entreprises, due chaque 15 décembre.",
            signal: 'Notification rouge « CFE impayée » à mi-décembre + majoration 10 %.',
            fix: 'La CFE est due dès la 2e année d\'activité, même sans bureau (basée sur la valeur locative du dépôt). Prévoyez ~1 500-3 000 €/an pour une PME transport.',
          },
          {
            title: 'Désactiver le provisionnement Midal sans filet de sécurité',
            mistake: "Vous recrutez Midal puis désactivez le toggle « Provisionnement fiscal automatique » pour libérer du cash apparent. Trois mois plus tard, l'acompte IS T2 tombe et la trésorerie est insuffisante.",
            signal: 'Notification rouge « Fonds insuffisants pour l\'acompte IS T2 » + crédit fiscal bancaire proposé à 4,5 % annuel + message « coup de gueule » de Midal dans la Messagerie en fin d\'année.',
            fix: "Si vous désactivez Midal, activez AU MOINS la Réserve fiscale 25 % (Livret A) pour avoir un filet. Idéal : laissez les deux ON et oubliez le sujet — c'est le rôle de Midal d'optimiser pour vous.",
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 2. FINANCE & COMPTABILITÉ
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'finance',
    title: 'Finance & Comptabilité',
    subtitle: 'Bilan, marge, EBE, runway',
    icon: 'TrendingUp',
    color: '#2563EB',
    pages: [
      {
        type: 'cover',
        title: 'Finance & Comptabilité',
        intro: "Lire un bilan en 30 secondes, calculer son point mort, anticiper sa trésorerie : autant de compétences qui font la différence entre une PME qui dure et une qui dépose le bilan.",
        bullets: [
          'Comptes de résultat vs bilan',
          'Marge brute vs marge nette',
          'Capitaux propres et solvabilité',
        ],
      },
      {
        type: 'definitions',
        title: 'Définitions essentielles',
        items: [
          { term: "Chiffre d'affaires (CA)", def: "Somme des ventes facturées HT sur une période. Indicateur de volume, pas de rentabilité." },
          { term: 'Marge brute', def: 'CA − coût des ventes directs (carburant, péages, salaires chauffeurs). Mesure la rentabilité des opérations.' },
          { term: 'Marge nette', def: "Bénéfice net ÷ CA. Inclut toutes les charges (fixes, financières, fiscales). En transport routier : 4-10 % normal." },
          { term: 'EBE (Excédent Brut d\'Exploitation)', def: "Capacité de l'entreprise à générer du cash avant impôts et amortissements. EBE = Marge − charges externes − salaires." },
          { term: 'Capitaux propres', def: 'Apports + réserves + résultat. Si négatifs → alerte rouge, banques refusent les crédits.' },
          { term: 'Actif', def: 'Ce que possède l\'entreprise (trésorerie, créances, véhicules). Toujours = Passif.' },
          { term: 'Passif', def: "Ce que l'entreprise doit (capitaux propres + dettes + fournisseurs)." },
          { term: 'Runway', def: 'Nombre de mois de trésorerie restante au rythme de dépenses actuel. Si < 3 mois → mesures correctives urgentes.' },
          { term: 'Amortissement linéaire', def: "Mode comptable par défaut : on étale la valeur d'un actif (camion, bureau, logiciel) sur sa durée de vie de manière égale. Ex. camion 60 000 € sur 5 ans = 12 000 €/an de dotation aux amortissements." },
          { term: 'Amortissement accéléré (dégressif)', def: "Mode optionnel autorisé sur les véhicules neufs et certains biens : on amortit plus vite les premières années (×1,75 le linéaire). Avantage : économie d'IS immédiate. Inconvénient : moins d'amortissement les années suivantes." },
          { term: 'VNC (Valeur Nette Comptable)', def: "Valeur d'un actif au bilan après amortissements cumulés. VNC = Prix d'achat − amortissements cumulés. Suivie mois par mois dans SCD sur le graphique d'évolution VNC." },
          { term: 'CFE (Cotisation Foncière des Entreprises)', def: "Impôt local dû dès la 2e année d'activité. Calculé sur la valeur locative des locaux (dépôt + bureau) × multiplicateur communal (0,8 à 1,6 selon la ville). Typiquement 1 500-3 000 €/an pour une PME transport. Échéance mi-décembre." },
          { term: 'Aides régionales (baux)', def: "Subvention récurrente de la région sur le loyer du dépôt ou bureau, débloquée selon la localisation choisie. Couvre jusqu'à 30 % du loyer mensuel. Visible dans l'onglet Finance et lors de l'achat/location d'un local." },
        ],
      },
      {
        type: 'formulas',
        title: 'Formules de calcul',
        items: [
          {
            name: 'Marge nette',
            formula: 'Marge nette (%) = (Bénéfice net ÷ CA) × 100',
            example: 'CA 600 000 € / Bénéfice net 42 000 € → Marge = 7 %.',
          },
          {
            name: 'EBE',
            formula: 'EBE = CA − Achats − Services externes − Salaires & charges',
            example: 'CA 50k − Carburant 12k − Sous-traitance 3k − Salaires 22k = EBE 13 000 €.',
          },
          {
            name: 'Capacité d\'autofinancement (CAF)',
            formula: "CAF = Résultat net + Dotations aux amortissements",
            example: "Résultat 30 000 € + Amortissements véhicules 18 000 € = CAF 48 000 €.",
          },
          {
            name: 'Ratio de solvabilité',
            formula: 'Solvabilité (%) = (Capitaux propres ÷ Actif total) × 100',
            example: "Actif 500k / Capitaux propres 150k → 30 % (sain ≥ 25 %).",
          },
          {
            name: 'Runway (mois)',
            formula: 'Runway = Trésorerie ÷ Charges mensuelles moyennes',
            example: "Trésorerie 80 000 € / Charges 25 000 €/mois → 3,2 mois (zone d'alerte).",
          },
          {
            name: 'Amortissement linéaire annuel',
            formula: "Dotation = Prix d'achat ÷ Durée d'utilisation\n(durée standard véhicule = 5 ans, bureau = 8 ans, logiciel = 3 ans)",
            example: "Camion 60 000 € sur 5 ans → 12 000 €/an, 1 000 €/mois.",
          },
          {
            name: 'Amortissement dégressif (accéléré)',
            formula: "Dotation N = VNC début N × (1/durée) × 1,75 (coefficient véhicule)",
            example: "Camion 60 000 €, 5 ans, coef 1,75 : An 1 = 60 000 × 0,20 × 1,75 = 21 000 €. Vs linéaire = 12 000 €. Économie d'IS immédiate.",
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Vocabulaire',
        items: [
          { word: 'Amortissement', def: "Étalement comptable du coût d'un véhicule sur sa durée de vie (5-7 ans pour un camion)." },
          { word: 'BFR', def: 'Besoin en Fonds de Roulement. Argent immobilisé dans le cycle d\'exploitation (créances clients − dettes fournisseurs).' },
          { word: 'Bilan', def: "Photographie du patrimoine à une date donnée. Actif = Passif." },
          { word: 'Compte de résultat', def: 'Film des produits et charges sur une période. Bénéfice = Produits − Charges.' },
          { word: 'Dotation', def: 'Charge non décaissée (amortissement, provision) qui réduit le résultat sans toucher la trésorerie.' },
          { word: 'Provision', def: 'Charge anticipée pour un risque probable (litige, créance douteuse).' },
          { word: 'Trésorerie', def: 'Cash disponible immédiatement sur les comptes courants.' },
        ],
      },
      {
        type: 'diagram',
        title: 'Le cycle de trésorerie',
        intro: "Entre l'instant où vous facturez et celui où vous encaissez, vous payez déjà carburant et salaires. C'est le besoin en fonds de roulement (BFR).",
        diagram: 'tresorerie-cycle',
        legend: [
          { label: 'Facture client', desc: 'Émise à la livraison — délai légal max 60 jours.' },
          { label: 'Encaissement',   desc: 'Cash entre 30 et 60 jours plus tard — pendant ce temps, vous payez tout.' },
          { label: 'Paie chauffeurs', desc: 'Sortie de cash mensuelle, fixe et incompressible.' },
          { label: 'Carburant',       desc: 'Sortie quasi-quotidienne. Premier poste de charges variables.' },
        ],
      },
      {
        type: 'cases',
        title: 'Cas pratiques',
        items: [
          {
            title: 'Calculer son point mort',
            situation: "Vous avez 18 000 €/mois de charges fixes (loyer dépôt, salaires fixes, mensualités crédit). Votre marge brute moyenne par contrat est de 35 %.",
            solution: "Point mort mensuel = Charges fixes ÷ taux de marge brute = 18 000 ÷ 0,35 = 51 430 € de CA. En dessous, vous perdez de l'argent.",
            lesson: 'Le point mort vous indique l\'objectif de CA MINIMUM pour ne pas couler. Affichez-le sur le Dashboard et comparez chaque semaine.',
          },
          {
            title: 'Décider d\'acheter un 3e camion',
            situation: "Vos 2 camions tournent à 95 % d'occupation. Un 3e camion coûterait 1 800 €/mois en mensualités + 1 200 € de charges fixes. Vous estimez +12 000 €/mois de CA potentiel à 40 % de marge.",
            solution: "Gain net attendu = 12 000 × 0,40 − 3 000 = 1 800 €/mois. Décision : OUI, l'investissement génère un excédent positif. Vérifiez la runway avant signature : il faut au moins 3 mois de trésorerie pour absorber la montée en charge.",
            lesson: 'Acheter quand l\'occupation dépasse 85 % sur 30 jours consécutifs. Vendre quand elle tombe sous 60 % sur 60 jours.',
          },
          {
            title: 'Détecter une dérive avant la crise',
            situation: "Votre runway est passée de 6 mois à 2,5 mois en 3 mois consécutifs.",
            solution: "Action immédiate : (1) geler les embauches, (2) renégocier avec la banque (différé partiel), (3) céder un véhicule sous-utilisé. NE PAS : prendre un nouveau crédit pour éponger les charges courantes — c'est la spirale.",
            lesson: 'La runway est le KPI vital. Suivez-la chaque mois. Pente descendante 3 mois de suite = alerte rouge, même si tout semble OK ailleurs.',
          },
          {
            title: 'Activer les aides régionales (loyer + CFE)',
            situation: "Vous embauchez 5 chauffeurs CDI nets sur l'année et investissez 60 k€ en CAPEX (3e dépôt + agrandissement). Vous comptez 3 fonctions support recrutées au 31 décembre.",
            solution: "Au 1er janvier suivant, le Conseil Régional vérifie automatiquement vos seuils et active 3 aides : (a) loyer HQ offert 12 mois (≈14 400 €/an économisés), (b) loyer bureau offert + 5 000 € FEDER one-shot, (c) CFE −15 % pendant 12 mois. Total annuel : ~20 000 € de charges effacées. Aucune démarche manuelle — c'est versé/déduit automatiquement.",
            lesson: "Surveillez l'onglet Finance → Loyers & Aides pour voir les seuils en temps réel. Les aides régionales se prennent en compte dans votre prévisionnel : ne lancez pas une grosse embauche au mois de mars si vous pouvez la décaler en novembre pour franchir le seuil de l'année N+1.",
          },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Pièges à éviter',
        items: [
          {
            title: 'Confondre bénéfice et trésorerie',
            mistake: 'Le compte de résultat affiche +12 000 €/mois de bénéfice. Vous payez un dividende. La trésorerie devient négative la semaine suivante.',
            signal: 'Écart > 30 % entre bénéfice net cumulé et trésorerie réelle = signal de BFR mal géré.',
            fix: "Avant tout dividende, vérifiez la CAF (capacité d'autofinancement) ET la projection de trésorerie sur 90 jours. Bénéfice ≠ cash disponible.",
          },
          {
            title: 'Ignorer les délais clients',
            mistake: 'Vous acceptez tous les contrats sans regarder les délais de paiement. Le grand compte vous paie à 60 jours.',
            signal: 'Poste « Créances clients » du bilan qui gonfle d\'un mois à l\'autre.',
            fix: "Négociez l'acompte 30 % à la signature pour les contrats > 5 000 €. Refusez les délais > 60 jours sauf assurance-crédit en place.",
          },
          {
            title: 'Sous-estimer les amortissements',
            mistake: "Vous regardez la marge brute (élevée car peu de charges directes) et oubliez que vos camions perdent 12 000 €/an de valeur.",
            signal: 'Marge brute > 40 % mais résultat net < 5 % = amortissements lourds ignorés dans la décision.',
            fix: "Calculez votre coût/km en INCLUANT l'amortissement véhicule (env. 0,12-0,18 €/km). Sinon vos prix de contrat ne couvrent pas le renouvellement de la flotte.",
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 3. BANQUE & CRÉDIT
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'banque',
    title: 'Banque & Crédit',
    subtitle: 'Taux, échéances, hypothèque, agios',
    icon: 'Landmark',
    color: '#059669',
    pages: [
      {
        type: 'cover',
        title: 'Banque & Crédit',
        intro: "Le levier bancaire accélère votre croissance — ou enfonce votre PME selon comment vous l'utilisez. Maîtrisez le coût total d'un crédit avant de signer.",
        bullets: [
          "Mécanique d'un crédit amortissable",
          'Coût réel : taux nominal vs TAEG',
          'Garanties : caution, hypothèque, gage',
        ],
      },
      {
        type: 'definitions',
        title: 'Définitions essentielles',
        items: [
          { term: 'Capital emprunté', def: 'Somme prêtée par la banque au démarrage. Sera remboursée par mensualités.' },
          { term: 'Taux nominal', def: 'Taux annuel affiché par la banque, hors frais.' },
          { term: 'TAEG', def: "Taux Annuel Effectif Global. Inclut intérêts + frais de dossier + assurance. C'est le VRAI coût du crédit." },
          { term: 'Mensualité', def: 'Échéance mensuelle fixe = part de capital + part d\'intérêts.' },
          { term: 'Amortissement', def: "Part de capital remboursée chaque mois. Augmente progressivement au fil du crédit." },
          { term: 'Intérêts', def: 'Rémunération de la banque. Diminuent progressivement (calculés sur le capital restant dû).' },
          { term: 'Agios', def: 'Frais facturés en cas de découvert non autorisé ou de dépassement. Typiquement 16-20 % annuels — à éviter absolument.' },
          { term: 'Hypothèque', def: 'Garantie sur un bien immobilier. La banque peut le saisir en cas de non-paiement.' },
          { term: 'Score de crédit (SCD)', def: "Note interne calculée par chaque banque (300-850) à partir de votre trésorerie, ratio d'endettement, ancienneté, régularité de paiement. Conditionne le taux proposé : excellent (>720) → taux préférentiel, moyen (600-720) → taux standard, faible (<600) → prêt refusé." },
          { term: 'Plafond d\'emprunt par banque', def: "Chaque banque applique son propre ratio « dette max / CA mensuel » (3,5 à 4,2 selon la banque) avec un plancher de CA qui scale avec l'âge de l'entreprise — environ 4 000 €/mois à J1, 8 000 €/mois à J80+. Diversifier sur 2-3 banques multiplie la capacité totale." },
          { term: 'Multi-bancarisation', def: "Stratégie consistant à répartir sa dette sur plusieurs banques. Réduit le risque de dépendance, améliore les conditions négociées et permet d'emprunter plus en cumulé." },
        ],
      },
      {
        type: 'formulas',
        title: 'Formules de calcul',
        items: [
          {
            name: 'Mensualité d\'un crédit (méthode classique)',
            formula: 'M = C × [t × (1+t)^n] / [(1+t)^n − 1]\noù t = taux mensuel, n = nombre d\'échéances, C = capital',
            example: "Capital 60 000 €, taux 4 % annuel sur 5 ans → mensualité ≈ 1 105 €.",
          },
          {
            name: 'Coût total du crédit',
            formula: 'Coût = (Mensualité × Nb échéances) − Capital emprunté',
            example: "60 000 € sur 5 ans à 4 % → 1 105 × 60 − 60 000 = 6 300 € d'intérêts cumulés.",
          },
          {
            name: 'Intérêts mensuels',
            formula: 'Intérêts du mois = Capital restant dû × (taux annuel ÷ 12)',
            example: "Capital restant 55 000 €, taux 4 % → 55 000 × 0,04 / 12 = 183 € d'intérêts ce mois-ci.",
          },
          {
            name: 'Ratio d\'endettement',
            formula: 'Endettement (%) = (Mensualités totales ÷ Revenus mensuels) × 100',
            example: "Mensualités 4 500 €, CA 25 000 € → 18 % (zone saine).",
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Vocabulaire',
        items: [
          { word: 'Caution', def: 'Personne ou société qui s\'engage à rembourser si l\'emprunteur ne paie pas.' },
          { word: 'Crédit-bail', def: 'Location longue durée d\'un véhicule avec option d\'achat finale. Comptablement plus souple qu\'un emprunt.' },
          { word: 'Découvert autorisé', def: 'Solde négatif toléré par la banque dans une limite contractuelle. Au-delà : agios majorés.' },
          { word: 'Différé d\'amortissement', def: "Période initiale où l'on ne paie que les intérêts. Allège la trésorerie au démarrage." },
          { word: 'Échéance', def: "Date à laquelle une mensualité doit être payée." },
          { word: 'Frais de dossier', def: 'Forfait à la signature (souvent 1 % du capital). Négociable.' },
          { word: 'Gage', def: "Garantie sur un bien mobilier (véhicule). La banque peut le récupérer en cas d'impayé." },
          { word: 'Indemnité de remboursement anticipé (IRA)', def: 'Pénalité si vous remboursez avant terme. Plafonnée à 6 mois d\'intérêts.' },
        ],
      },
      {
        type: 'diagram',
        title: "Amortissement d'un crédit",
        intro: "Au début, vous payez surtout des intérêts. Plus le temps passe, plus la part capital grandit. Comprendre cette courbe permet de mieux négocier la durée.",
        diagram: 'credit-amortissement',
        legend: [
          { label: 'Capital',  desc: 'Part de la mensualité qui rembourse réellement la dette.' },
          { label: 'Intérêts', desc: 'Coût payé à la banque. Diminue avec le temps (calculé sur le restant dû).', color: '#DC2626' },
        ],
      },
      {
        type: 'cases',
        title: 'Cas pratiques',
        items: [
          {
            title: '4 ans ou 7 ans pour un camion ?',
            situation: "Camion 80 000 € à 4 % d'intérêt. Mensualité 4 ans = 1 808 € (coût total 6 800 €). Mensualité 7 ans = 1 094 € (coût total 11 900 €).",
            solution: "7 ans = 700 € de mensualité gagnée chaque mois (+8 400 €/an de trésorerie). 4 ans = 5 100 € d'intérêts économisés. Choisissez selon la priorité : trésorerie (7 ans) ou coût (4 ans).",
            lesson: "Pour un camion qui doit travailler 5+ ans, étalez l'amortissement bancaire sur la durée de vie réelle de l'actif. Trésorerie > intérêts marginaux.",
          },
          {
            title: 'Anticiper un agio',
            situation: "Votre solde tombe à -8 000 € jeudi. Votre découvert autorisé est 5 000 €. Encaissement client prévu mardi suivant (4 jours plus tard).",
            solution: "Agios = 8 000 € × 18 %/an × 4/365 = ~16 €. Mais 3 000 € au-dessus du découvert autorisé → forfait fixe 35 € + agios majorés. Coût total ~70 €. Solution : appelez la banque pour un dépassement ponctuel autorisé (souvent gratuit).",
            lesson: "Un coup de fil à votre conseiller coûte moins cher qu'un agio. Anticipez chaque fois qu'un encaissement majeur dérape.",
          },
          {
            title: 'Refinancement opportuniste',
            situation: "Vous avez un crédit à 5,5 % qu'il vous reste 3 ans à rembourser (capital restant 30 000 €). Les taux du marché sont à 3,2 %.",
            solution: "Économie brute = (5,5 % − 3,2 %) × 30k × 3 = 2 070 €. IRA max = 6 mois d'intérêts à 5,5 % = ~825 €. Frais de dossier nouveau crédit ~300 €. Économie nette ≈ 950 €. Décision : OUI, à négocier en ferme.",
            lesson: "Refinancez quand les taux chutent de > 1,5 point. Faites toujours le calcul net après IRA + frais de dossier.",
          },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Pièges à éviter',
        items: [
          {
            title: 'Comparer sur le taux nominal au lieu du TAEG',
            mistake: 'Banque A propose 3,5 % nominal + 1 % frais de dossier. Banque B propose 3,8 % nominal sans frais. Vous prenez A « moins cher ».',
            signal: "Écart TAEG > 0,5 point malgré un taux nominal plus bas = frais de dossier ou assurance qui gonflent le coût réel.",
            fix: "Comparez UNIQUEMENT le TAEG. C'est le seul taux qui intègre tout (intérêts + frais + assurance).",
          },
          {
            title: 'Empiler les crédits sans plafonner l\'endettement',
            mistake: 'Vous prenez un 3e crédit véhicule. Vos mensualités totalisent 38 % de votre CA mensuel.',
            signal: 'Indicateur « Ratio d\'endettement » > 30 % au Dashboard finance.',
            fix: "Plafond prudent : 25 % du CA mensuel en mensualités cumulées. Au-delà de 30 %, refusez tout nouveau crédit même si la banque vous l'accorde.",
          },
          {
            title: 'Hypothéquer le dépôt pour un besoin court terme',
            mistake: "Vous gagez votre entrepôt pour financer 6 mois de carburant. La banque saisira en cas d'impayé.",
            signal: 'Vous donnez une garantie REELLE pour une dette OPERATIONNELLE = signal de surendettement structurel.',
            fix: "L'hypothèque est faite pour les investissements LONGS (immobilier, équipement durable). Pour la trésorerie, négociez un crédit court terme (CT) ou de la cession Dailly (cessions de créances).",
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 4. RH & CLIMAT SOCIAL
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'rh',
    title: 'RH & Climat social',
    subtitle: 'Satisfaction, burnout, compétence',
    icon: 'Users',
    color: '#D97706',
    pages: [
      {
        type: 'cover',
        title: 'RH & Climat social',
        intro: "Un chauffeur démissionnaire, c'est 8 000 € de coût caché (recrutement, formation, perte d'efficacité). Investir dans le climat social = retour direct sur la performance.",
        bullets: [
          'Satisfaction : moteur de la fidélité',
          'Burnout : signal précoce à détecter',
          'Compétence vs ancienneté',
        ],
      },
      {
        type: 'definitions',
        title: 'Définitions essentielles',
        items: [
          { term: 'Satisfaction (0-100)', def: 'Score de bien-être d\'un chauffeur. < 40 : démission probable. > 70 : performance maximale.' },
          { term: 'Compétence (0-100)', def: 'Niveau technique du chauffeur. Augmente avec les missions et formations. Impact direct sur la qualité de livraison.' },
          { term: 'Burnout', def: "Épuisement professionnel. Détecté automatiquement à partir d'une combinaison de faible satisfaction + missions intenses." },
          { term: 'Coût employeur', def: "Salaire brut + charges patronales (~42 %) + tickets restau + mutuelle. C'est ce que le chauffeur coûte vraiment à l'entreprise." },
          { term: 'Renégociation salariale', def: 'Quand un chauffeur senior demande une revalorisation. Refuser = chute satisfaction. Accepter = +15 % de coût.' },
          { term: 'Spécialités', def: "Compétences spécifiques (frigorifique, longue distance, ADR, urbain). Débloquent l'accès à certains contrats premium." },
          { term: 'XP / Niveau', def: "Expérience cumulée. Junior → Confirmé → Senior. Senior = +25 % de productivité, mais demande de salaire plus haute." },
          { term: 'Permis (B / C1 / C / CE)', def: "Catégorie de permis poids-lourd détenue par le chauffeur. Détermine quels véhicules il peut conduire : B = utilitaire < 3,5 t (T1), C = camion solo > 7,5 t (T2), CE = poids-lourd + remorque (T3, T4 et au-delà). Hiérarchique : un chauffeur CE peut conduire tous les véhicules." },
          { term: 'Validation permis', def: "Règle métier dure : le moteur refuse l'assignation d'un véhicule si le chauffeur n'a pas le permis requis. Le bandeau jaune affiche le permis manquant pour aiguiller le joueur (former le chauffeur ou recruter quelqu'un d'autre)." },
          // ── 02/06/2026 — Obligations légales françaises ──
          { term: 'CDI / CDD', def: "CDI = Contrat à Durée Indéterminée, le contrat de référence (95 % du transport). CDD = Contrat à Durée Déterminée, obligatoirement écrit, motif imposé (remplacement, surcroît d'activité). À la fin d'un CDD : prime de précarité 10 % du brut versé." },
          { term: "Période d'essai", def: "Phase de test au début d'un CDI : 2 mois pour un employé (renouvelable une fois). Rupture libre des deux côtés, sans indemnité légale, mais avec un délai de prévenance proportionnel à l'ancienneté (24 h à 1 mois). Au-delà de 8 mois total, la rupture devient une rupture classique." },
          { term: 'Rupture conventionnelle', def: "Rupture du CDI à l'amiable, négociée entre employeur et salarié et HOMOLOGUÉE par la DREETS. Pas de motif à justifier, pas de préavis, mais une **indemnité spécifique de rupture conventionnelle (ISRC)** au moins égale à l'indemnité légale de licenciement. Mode privilégié dans le jeu : pas de litige prud'homal." },
          { term: 'Licenciement (cause réelle et sérieuse)', def: "Rupture du CDI à l'initiative de l'employeur, motivée (économique, faute, inaptitude). Procédure stricte : convocation → entretien préalable → notification. Préavis de 2 mois pour un cadre. Indemnité légale identique à la rupture conventionnelle. Risque de contestation prud'homale si la cause est jugée non sérieuse." },
          { term: 'ISRC — Indemnité Spécifique de Rupture Conventionnelle', def: "Plancher légal : ¼ mois de salaire brut × ancienneté pour les 10 premières années, puis ⅓ mois × année au-delà. Non soumise aux cotisations sociales jusqu'à 2 PASS (~94 000 €), mais soumise à la CSG/CRDS et au forfait social (~30 % du dépassement)." },
          { term: 'FCO — Formation Continue Obligatoire', def: "35 heures à renouveler tous les **5 ans** pour tout conducteur professionnel > 3,5 t. Sans FCO valide, le chauffeur ne peut PAS rouler en règle. Amende DREAL en cas de contrôle : 800 à 1 500 € + immobilisation possible." },
          { term: 'Visite médicale d\'aptitude', def: "Obligatoire pour tout conducteur poids-lourd, à renouveler tous les **5 ans** avant 60 ans, puis tous les **2 ans**. Réalisée par un médecin agréé par la préfecture. Sans visite valide, le permis pro est suspendu." },
          { term: 'ADR — Accord européen', def: "Certification spécialisée pour le transport de matières dangereuses (chimie, carburants, batteries). 5 jours initiaux + recyclage tous les 5 ans. Sans certif valide, transport de matières dangereuses interdit." },
          { term: 'CACES R489 / R372', def: "Certificat d'Aptitude à la Conduite En Sécurité (chariot, grue, nacelle). Recyclage **tous les 5 ans**. Indispensable pour manutention en entrepôt ou chantier BTP." },
          { term: 'DUERP — Document Unique', def: "Document Unique d'Évaluation des Risques Professionnels. Obligatoire dès le 1er salarié, mise à jour annuelle. Tient à jour les risques identifiés par poste (manutention, conduite, chargement)." },
          { term: 'Préavis', def: "Période obligatoire entre l'annonce de la rupture et la fin du contrat. Démission : 1 mois (ouvrier) / 2 mois (cadre). Licenciement : 1 mois (ancienneté < 2 ans) / 2 mois (≥ 2 ans). Rupture conventionnelle : **pas de préavis** — c'est l'un de ses avantages." },
          { term: 'Cotisations patronales', def: "Charges sociales payées par l'employeur sur le salaire brut : URSSAF (santé, retraite, chômage, famille) ≈ 42 %. C'est pour ça que `Coût employeur ≈ Salaire brut × 1,42`." },
        ],
      },
      {
        type: 'formulas',
        title: 'Formules de calcul',
        items: [
          {
            name: 'Coût employeur mensuel',
            formula: 'Coût = Salaire brut × (1 + 0,42) + avantages mensuels',
            example: "Salaire brut 2 200 € + 50 € tickets restau + 30 € mutuelle = 2 200 × 1,42 + 80 = 3 204 €.",
          },
          {
            name: 'Coût de remplacement (coût caché)',
            formula: 'Remplacement ≈ 3 mois de salaire chargé + perte de productivité',
            example: "Coût employeur 3 200 €/mois × 3 = 9 600 € (visible) + 2 000 € (formation/perte) = ~11 600 €.",
          },
          {
            name: 'Score score performance chauffeur',
            formula: 'Score = compétence + xp×0,001 + trips×0,05 + satisfaction×0,02',
            example: 'Compétence 75 + 1500 XP + 80 trips + 85 sat = 75 + 1,5 + 4 + 1,7 = 82,2.',
          },
          {
            name: 'Productivité réelle',
            formula: 'Productivité = (compétence ÷ 100) × (satisfaction ÷ 100) × 100 %',
            example: 'Compétence 80 × Satisfaction 60 = 0,80 × 0,60 = 48 % de productivité effective.',
          },
          // ── 02/06/2026 — Obligations légales : calcul des indemnités ──
          {
            name: 'Indemnité ISRC (rupture conventionnelle / licenciement légal)',
            formula: 'ISRC = (¼ × brut mensuel × ancienneté[0-10 ans]) + (⅓ × brut mensuel × ancienneté[> 10 ans])',
            example: "Salaire brut 3 000 €, 5 ans d'ancienneté → 0,25 × 3 000 × 5 = 3 750 €. Pour 15 ans → 0,25 × 3 000 × 10 + (1/3) × 3 000 × 5 = 7 500 + 5 000 = 12 500 €.",
          },
          {
            name: 'Coût total employeur d\'une rupture',
            formula: 'Coût = ISRC + Préavis (si licenciement) + Solde CP + Charges sur indemnités',
            example: "Rupture conv. 5 ans, brut 3 000 € : ISRC 3 750 € + Préavis 0 € + CP ~300 € + Charges 12 % ≈ 450 € + 42 % du solde CP ≈ 125 € → **~4 625 €**. Le même en licenciement avec préavis 2 mois : ~12 000 € (CR : 8 500 € de plus).",
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Vocabulaire',
        items: [
          { word: 'AGS', def: 'Association pour la Gestion du régime de garantie des créances des Salariés. Avance les salaires impayés en cas de liquidation.' },
          { word: 'Compatibilité chauffeur/véhicule', def: "Affinité entre un chauffeur et un type de camion. Booste la satisfaction (+5) si bien matché." },
          { word: 'Convention collective', def: "Règles métier complémentaires au Code du travail. Pour le transport : CCN 1979." },
          { word: 'Démission', def: 'Rupture à l\'initiative du salarié. Pas d\'indemnité, préavis 1 mois.' },
          { word: 'Indemnité de licenciement', def: '¼ mois par année d\'ancienneté jusqu\'à 10 ans, ⅓ au-delà. Versée en cas de licenciement non-faute grave.' },
          { word: 'Trait de personnalité', def: 'Particularité comportementale (perfectionniste, sociable, indépendant). Affecte la satisfaction selon le contexte.' },
        ],
      },
      {
        type: 'diagram',
        title: 'Satisfaction → démission',
        intro: "La satisfaction n'est pas linéaire : sous 40, le chauffeur cherche activement ailleurs. La pente s'accélère brutalement.",
        diagram: 'satisfaction-curve',
        legend: [
          { label: 'Productif (60-100)', desc: 'Le chauffeur livre, accepte les heures sup, recommande la boîte.', color: '#10B981' },
          { label: 'Alerte (40-60)',     desc: 'Productivité baisse, retards, conflits — l\'avertissement est là.', color: '#F59E0B' },
          { label: 'Démission (< 40)',   desc: 'Probabilité de départ qui double tous les 30 jours sous ce seuil.', color: '#DC2626' },
        ],
      },
      {
        type: 'cases',
        title: 'Cas pratiques',
        items: [
          {
            title: 'Demande de salaire d\'un senior',
            situation: "Karim, votre meilleur chauffeur (compétence 85, satisfaction 70, 3 ans d'ancienneté) demande +200 €/mois. Il gagne déjà 200 € de plus que la moyenne marché.",
            solution: "Refus simple → satisfaction -15 (passage à 55) → productivité chute. Acceptation → coût employeur +284 €/mo (×1,42 charges). Contre-offre : +100 € + un jour de congé supplémentaire = perception de respect + coût maîtrisé.",
            lesson: "Les contre-offres non-monétaires (formation, congés, véhicule plus récent) valent souvent autant qu'un cash supplémentaire et coûtent 3× moins.",
          },
          {
            title: 'Prévenir un burnout',
            situation: "Sarah a une satisfaction qui chute de 80 à 50 en 2 mois. Elle a fait 18 trips en mai (moyenne 12). Elle vient de refuser un weekend.",
            solution: "Signaux croisés : surcharge + refus = burnout imminent. Action immédiate : -1 mission/semaine pendant 4 semaines + entretien (5 min) + 1 jour de congé exceptionnel. Coût : ~600 € de manque à gagner. Évite ~11 600 € de coût de remplacement.",
            lesson: "Repérez les ratios satisfaction qui baissent VITE (>10 pts/mois). Une intervention précoce coûte 20× moins cher qu'un remplacement.",
          },
          {
            title: 'Recruter junior ou senior ?',
            situation: "Vous avez un budget de 3 000 €/mois (coût employeur). Vous hésitez entre un junior (compétence 50, ~2 600 € coût) et un senior (compétence 85, ~3 200 € coût).",
            solution: "Junior : productivité ~50 %, marge nette ~300 €/mo de revenu vs coût. Senior : productivité ~85 %, marge ~800 €/mo. Senior dégage 2,6× plus de marge mais demande 230 € de plus à recruter.",
            lesson: "Sur les contrats à forte valeur ajoutée (premium, ADR, longue distance), un senior rentabilise vite son surcoût. Pour les tournées urbaines simples, les juniors suffisent.",
          },
          // ── 02/06/2026 — Cas pratique : Rupture conv. vs Licenciement ──
          {
            title: 'Séparer d\'un chauffeur : rupture conventionnelle ou licenciement ?',
            situation: "Un chauffeur de 4 ans d'ancienneté (brut 3 000 €/mois) ne convient plus : performances faibles mais pas de faute grave. Vous voulez vous en séparer proprement.",
            solution: "Licenciement « cause réelle et sérieuse » → procédure de 1 mois minimum (convocation, entretien, notification), préavis 2 mois (~ 8 500 € brut chargé), ISRC ~3 000 €, risque prud'homal. Total : ~12-15 000 €. Rupture conventionnelle → 1 entretien + homologation DREETS (15 jours), ISRC ~3 000 €, **pas de préavis**, **pas de litige possible**. Total : ~4 500 €.",
            lesson: "Pour une séparation sans conflit, la **rupture conventionnelle économise 60-70 % du coût total** et zéro risque prud'homal. Le licenciement classique se justifie quand il y a une vraie faute documentée OU pour un motif économique.",
          },
          {
            title: 'FCO arrive à échéance : agir avant ou laisser passer ?',
            situation: "La FCO de votre chauffeur Karim expire dans 60 jours. La formation coûte 600 € et l'immobilise 5 jours. Vous avez 3 contrats en cours.",
            solution: "Lancer la FCO maintenant : 600 € + 5 jours d'arrêt = ~1 800 € de manque à gagner. Total ~2 400 €. Attendre que ça périme : amende DREAL 800-1 500 € au prochain contrôle (~1 mois) + immobilisation immédiate du camion → annulation des contrats en cours → pénalités clients ~5 000 € + rep -3.",
            lesson: "Toutes les formations HSE périment silencieusement et coûtent **3-5× plus cher en réactif qu'en préventif**. Mettez en place une revue trimestrielle des échéances FCO / médical / ADR / CACES depuis la page RSE.",
          },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Pièges à éviter',
        items: [
          {
            title: 'Refuser systématiquement les augmentations',
            mistake: "Vous voyez chaque demande comme une attaque. Vous refusez toutes pour montrer votre fermeté.",
            signal: 'Plusieurs chauffeurs sous 60 de satisfaction simultanément + messages « renégociation » accumulés en attente.',
            fix: "Construisez une grille salariale TRANSPARENTE (junior/confirmé/senior × spécialités). Acceptez automatiquement les demandes ≤ 5 % qui correspondent à un palier d'ancienneté franchie.",
          },
          {
            title: 'Surcharger un chauffeur fiable',
            mistake: "Karim accepte tout. Vous lui collez les contrats compliqués et les weekends. Il craque au bout de 3 mois.",
            signal: 'Ratio trips/mois > 16 pour un chauffeur + satisfaction qui décroche.',
            fix: "Plafonnez à 14 trips/mois par chauffeur. Tournez les missions difficiles entre 2-3 personnes pour répartir la fatigue.",
          },
          {
            title: 'Ne pas former',
            mistake: "Pas de budget formation. Vos juniors restent juniors. Vos seniors démissionnent pour ailleurs (où ils sont formés).",
            signal: 'Aucune formation lancée depuis > 6 mois + 1+ démission senior.',
            fix: "Budget formation 1-2 % du CA RH. ADR / Frigorifique / Tachy = compétences qui débloquent des contrats à +25 % de marge ET fidélisent le chauffeur.",
          },
          // ── 02/06/2026 — Pièges légaux ──
          {
            title: 'Licencier sans cause réelle et sérieuse',
            mistake: "Vous licenciez un chauffeur pour « manque d'envie » ou « entente difficile ». Aucun écrit, aucun avertissement, aucun motif documenté.",
            signal: 'Procédure expéditive, pas de mail de mise en garde dans le dossier, motif vague dans la lettre.',
            fix: "Toute la procédure de licenciement doit être DOCUMENTÉE par écrit (mails, comptes-rendus). Sans cause réelle et sérieuse, le conseil de prud'hommes condamne typiquement à **6-12 mois de salaire en dommages-intérêts**. Préférez la **rupture conventionnelle** : pas de motif à prouver, pas de contestation possible.",
          },
          {
            title: 'Laisser les formations HSE expirer (FCO / Médical / ADR / CACES)',
            mistake: "Vos chauffeurs roulent avec une FCO périmée depuis 3 mois. Vous prévoyez de les renvoyer en formation « bientôt ».",
            signal: "Badge rouge « expirée » sur la page RSE de Claire Maréchal. Amendes DREAL aléatoires qui s'accumulent.",
            fix: "Programmez les renouvellements **60 jours AVANT échéance** (la page RSE alerte automatiquement à J-60). Ne tolérez aucune formation HSE expirée : 1 contrôle DREAL = 800-1 500 € + image dégradée + risque pénal pour le dirigeant.",
          },
          {
            title: "Confondre période d'essai et CDI ferme",
            mistake: "Au bout de 7 mois vous voulez « licencier facilement » comme en période d'essai. Mais le bail est devenu un CDI ferme automatiquement.",
            signal: "Ancienneté > 8 mois mais vous n'avez jamais renouvelé formellement la période d'essai (qui est limitée à 2 mois + 2 mois renouvelée).",
            fix: "La période d'essai est limitée à 2 mois (employé) ou 4 mois (cadre). Le renouvellement doit être écrit ET prévu par la convention collective. Au-delà, c'est un CDI ferme : rupture = rupture conventionnelle ou licenciement classique avec procédure complète.",
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 5. TRANSPORT & LOGISTIQUE
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'transport',
    title: 'Transport & Logistique',
    subtitle: 'Incoterms, TVA, flotte, leasing',
    icon: 'Truck',
    color: '#0891B2',
    pages: [
      {
        type: 'cover',
        title: 'Transport & Logistique',
        intro: 'Le métier exige de jongler entre 3 dimensions : réglementaire (Incoterms, TVA), opérationnelle (flotte, occupation) et financière (achat vs leasing). On les voit ici.',
        bullets: [
          'Incoterms : qui paie quoi à quel moment ?',
          'TVA intra vs extra communautaire',
          'Achat ferme, crédit, leasing : 3 stratégies',
        ],
      },
      {
        type: 'definitions',
        title: 'Définitions essentielles',
        items: [
          { term: 'Incoterm', def: "Acronyme international qui définit la répartition des frais, risques et obligations entre vendeur et acheteur sur un transport." },
          { term: 'EXW', def: "Ex Works — l'acheteur prend en charge depuis l'usine du vendeur. Risque maximum pour l'acheteur." },
          { term: 'FCA', def: "Free Carrier — vendeur livre au transporteur désigné. Le risque bascule à cet endroit." },
          { term: 'DAP', def: 'Delivered at Place — vendeur livre à destination, dédouanement à la charge de l\'acheteur.' },
          { term: 'DDP', def: "Delivered Duty Paid — vendeur livre dédouané. Risque maximum pour le vendeur." },
          { term: 'TVA intracommunautaire', def: "Sur livraison UE B2B : autoliquidation. Le client déclare la TVA dans son pays." },
          { term: 'TVA extracommunautaire', def: "Export hors UE : TVA française non applicable. Droits de douane potentiels à destination." },
          { term: 'Taux d\'occupation', def: 'Part de la flotte effectivement utilisée. < 60 % = sous-utilisation chronique. > 85 % = saturation, refus de contrats.' },
        ],
      },
      {
        type: 'diagram',
        title: 'Incoterms 2020 : où basculent frais et risques ?',
        intro: "Chaque Incoterm définit deux choses : à quel moment les frais basculent du vendeur à l'acheteur, et à quel moment le risque (perte, vol, dommage) bascule. EXW = tout à la charge de l'acheteur. DDP = tout à la charge du vendeur. Entre les deux, 4 grandes catégories.",
        diagram: 'incoterm-bands',
        legend: [
          { label: 'EXW (Ex Works)', desc: 'Vendeur livre dans son usine. Acheteur paie chargement, transport, douane export + import.' },
          { label: 'FCA (Free Carrier)', desc: 'Vendeur livre au transporteur. Le risque bascule à cet endroit.' },
          { label: 'CPT/CIP (Carriage Paid To)', desc: 'Vendeur paie le transport jusqu’à destination — mais le risque bascule au départ.' },
          { label: 'DAP (Delivered at Place)', desc: 'Vendeur livre à destination, dédouanement import à la charge de l’acheteur.' },
          { label: 'DDP (Delivered Duty Paid)', desc: 'Vendeur livre dédouané chez l’acheteur. Risque maximum pour le vendeur.' },
        ],
      },
      {
        type: 'formulas',
        title: 'Formules de calcul',
        items: [
          {
            name: 'Coût kilométrique total',
            formula: 'Coût km = (Carburant + Péages + Maintenance + Amortissement véhicule + Salaire chauffeur ÷ km/mois)',
            example: 'Pour 1 camion 19t : ~0,90 €/km de coût total tout compris.',
          },
          {
            name: 'Marge contrat',
            formula: 'Marge = (Prix contrat − coût total mission) ÷ Prix contrat × 100',
            example: "Contrat 1 200 € sur 800 km à 0,90 €/km = 720 € de coût → Marge = (1200-720)/1200 = 40 %.",
          },
          {
            name: 'TVA sur livraison intra-UE',
            formula: 'Si client B2B avec n° TVA valide : TVA = 0 € (autoliquidation client)\nSinon : TVA française 20 %',
            example: "Livraison Lyon → Milan pour Fiat (TVA italienne valide) : 0 € de TVA collectée.",
          },
          {
            name: 'Comparatif achat vs leasing (3 ans)',
            formula: 'Coût achat = Prix × (1 - revente) + intérêts crédit\nCoût leasing = Loyer × 36',
            example: "Camion 60 000 € : achat = 60k - 25k revente = 35k. Leasing = 1 100 × 36 = 39 600 €. Achat -10 %.",
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Vocabulaire',
        items: [
          { word: 'ADR', def: 'Accord européen pour le transport de matières dangereuses. Nécessite un certificat chauffeur.' },
          { word: 'CMR', def: "Lettre de voiture internationale. Document obligatoire pour tout transport routier transfrontalier." },
          { word: 'Cabotage', def: "Transport intérieur dans un pays autre que celui d'immatriculation du camion. Limité à 3 missions sur 7 jours en UE." },
          { word: 'DREAL', def: 'Direction Régionale de l\'Environnement, de l\'Aménagement et du Logement. Effectue les contrôles techniques en transport.' },
          { word: 'Eurovignette', def: "Péage forfaitaire dans certains pays UE (Allemagne, Pays-Bas, Luxembourg) pour les PL." },
          { word: 'PTAC', def: 'Poids Total Autorisé en Charge. Détermine la catégorie du véhicule (3,5t — 19t — 26t — 44t).' },
          { word: 'TMS', def: "Transport Management System. Logiciel de planification et suivi des opérations." },
          { word: 'Tachygraphe', def: "Boîtier obligatoire enregistrant les temps de conduite et repos du chauffeur." },
        ],
      },
      {
        type: 'diagram',
        title: 'Acheter, créditer ou louer ?',
        intro: "Sur 3 ans, le mode de financement change le coût total — et l'impact sur le bilan. Voici une comparaison à dimensionner pour CHAQUE véhicule.",
        diagram: 'flotte-comparatif',
        legend: [
          { label: 'Achat comptant', desc: 'Le moins cher long terme mais ponctionne la trésorerie immédiatement.' },
          { label: 'Leasing',        desc: 'Le plus cher (+10-15 %) mais aucune sortie de cash initiale.', color: '#DC2626' },
          { label: 'Crédit',         desc: 'Compromis : étale la sortie + reste propriétaire du bien.', color: '#10B981' },
        ],
      },
      {
        type: 'cases',
        title: 'Cas pratiques',
        items: [
          {
            title: 'Livraison Lyon → Milan (Fiat)',
            situation: "Vous facturez 1 800 € HT à Fiat (intra-UE B2B avec numéro TVA italien valide). Coût mission : 1 100 €.",
            solution: "Autoliquidation : vous facturez 1 800 € HT sans TVA française. Fiat déclare la TVA italienne dans son pays. Marge brute = (1800 − 1100) / 1800 = 39 %. Mention obligatoire sur facture : « Autoliquidation art. 196 directive 2006/112/CE ».",
            lesson: "Vérifiez TOUJOURS le numéro TVA intracom du client via VIES.europa.eu avant la première facture. Un numéro invalide = TVA française à reverser de votre poche.",
          },
          {
            title: 'Quel Incoterm choisir ?',
            situation: "Vous transportez du vin de Bordeaux pour un client suisse. La douane à Genève est compliquée.",
            solution: "DDP (Delivered Duty Paid) = vous gérez tout, y compris la douane suisse → prix élevé mais simple pour le client. DAP = vous livrez, mais le client gère la douane → moins cher pour vous, plus de risque pour le client. Recommandation : DAP avec courtier dédié.",
            lesson: "Un Incoterm clair sur la facture évite 90 % des litiges. EXW (le moins risqué pour le vendeur) — DDP (le plus risqué).",
          },
          {
            title: 'Optimiser le taux d\'occupation',
            situation: "Vos 5 camions tournent à 55 % d'occupation. Vous perdez 800 €/mois par camion en charges fixes non couvertes.",
            solution: "Action 1 : refuser temporairement les contrats premium à délai serré qui occupent mal. Action 2 : pricer plus agressivement (-5 %) pour gagner des contrats remplisseurs. Action 3 (radical) : céder 1 camion pour passer à 4 à 75 % d'occupation = +50 % de marge.",
            lesson: "Une flotte sous-utilisée = trésorerie qui s'évapore. La taille n'est pas l'objectif — l'occupation l'est.",
          },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Pièges à éviter',
        items: [
          {
            title: 'Sur-acheter en haut de cycle',
            mistake: "L'activité explose, vous commandez 3 camions neufs. 6 mois plus tard, l'activité retombe et 2 camions tournent à vide.",
            signal: 'Croissance CA > 25 % sur un trimestre + achat de flotte sans test de durabilité.',
            fix: "Avant chaque achat, exigez 60 jours d'occupation > 85 % CONSÉCUTIFS sur les camions existants. Si la demande explose, prenez du leasing court (12 mois) pour vérifier la durabilité.",
          },
          {
            title: 'Ignorer le cabotage',
            mistake: 'Votre camion immatriculé en France fait 5 missions consécutives en Allemagne en 1 semaine.',
            signal: 'Notification douane « Cabotage abusif détecté » + amende 1 000-5 000 €.',
            fix: "Limite UE : 3 missions intra-pays sur 7 jours pour un camion étranger. Au-delà, retournez vide au pays d'immatriculation ou facturez via une filiale locale.",
          },
          {
            title: 'Confondre marge brute et marge contrat',
            mistake: "Vous calculez la marge globale entreprise. Vous acceptez un contrat à 5 % de marge brute pensant que ça contribue.",
            signal: 'Contrat dont la marge brute < marge brute moyenne entreprise = signal de dilution.',
            fix: "Chaque contrat doit avoir une marge >= 30 % brute. En dessous, vous diluez la performance ET vous mobilisez une ressource pour rien.",
          },
          {
            title: 'Saturer son tableau de bord avec trop d\'opportunités',
            mistake: "Vous laissez s'accumuler 20+ contrats dans la liste des opportunités. Le bruit visuel cache les vraies pépites et vous perdez 10 min à chaque revue.",
            signal: "Plus de 15 contrats simultanés dans « Contrats disponibles » + plusieurs contrats que vous savez d'avance ne jamais accepter (trop courts, mauvaise spécialité, marge < seuil).",
            fix: "Utilisez le bouton **X** sur chaque contrat pour le masquer immédiatement (action réversible via « Restaurer N »). Gardez 5-7 opportunités à l'écran. Ce filtre rapide divise par 3 le temps de décision sans perdre les contrats — ils restent juste hors du champ visuel.",
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 6. M&A & STRATÉGIE
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'ma',
    title: 'M&A & Stratégie',
    subtitle: 'Due diligence, LOI, valorisation, antitrust',
    icon: 'Handshake',
    color: '#DB2777',
    pages: [
      {
        type: 'cover',
        title: 'M&A & Stratégie',
        intro: "Racheter un concurrent peut multiplier votre taille en quelques mois — ou vous enterrer dans la dette si vous payez trop cher. L'art du M&A est de payer le juste prix au bon moment.",
        bullets: [
          'Les 5 étapes d\'une acquisition',
          'Calcul de valorisation',
          'Risques antitrust et rétention chauffeurs',
        ],
      },
      {
        type: 'definitions',
        title: 'Définitions essentielles',
        items: [
          { term: 'M&A', def: "Mergers & Acquisitions — fusions et acquisitions. Croissance externe par rachat d'un concurrent." },
          { term: 'Due diligence', def: "Audit complet de la cible avant d'engager : finances, contrats, RH, contentieux. Première étape." },
          { term: 'LOI', def: "Lettre d'Intention. Document non engageant qui formalise les négociations et garantit une exclusivité temporaire." },
          { term: 'Valorisation', def: 'Prix considéré comme juste pour la cible. Plusieurs méthodes : multiple de CA, multiple d\'EBE, DCF.' },
          { term: 'Antitrust', def: "Réglementation anti-concentration. L'Autorité de la Concurrence peut bloquer un rachat qui crée un monopole local." },
          { term: 'Closing', def: "Signature finale chez le notaire. Transfert effectif des actifs et du contrôle." },
          { term: 'Earn-out', def: 'Complément de prix conditionné à l\'atteinte d\'objectifs post-rachat. Sécurise l\'acheteur.' },
          { term: 'Goodwill', def: 'Survaleur payée au-delà des actifs nets comptables, en échange de la marque, des contrats, de l\'équipe.' },
        ],
      },
      {
        type: 'formulas',
        title: 'Formules de calcul',
        items: [
          {
            name: 'Prix de rachat indicatif (méthode SCD)',
            formula: 'Prix = 0,5 × CA_annuel + 0,8 × Flotte_valeur_revente + 1,2 × Contrats_actifs_valeur + Dette_nette',
            example: 'CA 60k/mois × 12 = 720k → 0,5 × 720k = 360k\n10 véhicules à 35k = 350k → 0,8 × 350k = 280k\n15 contrats à 4,5k = 67,5k → × 1,2 = 81k\nDette nette : 80k\nTotal ≈ 801 000 €',
          },
          {
            name: 'Multiple d\'EBE (méthode classique)',
            formula: 'Valorisation = EBE × multiple (transport : 4-6x)',
            example: 'EBE 80 000 €/an × multiple 5 = 400 000 € de valorisation.',
          },
          {
            name: 'Part de marché combinée (antitrust)',
            formula: 'PDM_combinée = (CA_acheteur + CA_cible) ÷ CA_marché_local',
            example: 'CA 40k + 30k = 70k / Marché 200k = 35 % → zone amber (examen attentif).',
          },
          {
            name: 'Taux de rétention chauffeurs post-acquisition',
            formula: 'Rétention = base_satisfaction + modifier_réputation_acheteur\nbase : sat>60 → 90 % | 40-60 → 60 % | <40 → 25 %',
            example: 'Acheteur réputé 80/100 : modifier = +6 %\nChauffeur sat 75 → 96 % de chances de rester.',
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Vocabulaire',
        items: [
          { word: 'Asset deal', def: "Rachat d'actifs ciblés (flotte, contrats) sans reprendre la société entière. Plus souple fiscalement." },
          { word: 'Audit juridique', def: 'Vérification des statuts, contrats, propriété intellectuelle, contentieux en cours.' },
          { word: 'Cession de fonds', def: "Vente du fonds de commerce : clientèle, enseigne, contrats, matériel. Plus large qu'un asset deal." },
          { word: 'Clause de non-concurrence', def: "Engagement du vendeur à ne pas reprendre la même activité dans une zone donnée pendant X années." },
          { word: 'Concentration', def: "Opération qui modifie durablement le contrôle d'une entreprise. Doit être notifiée si seuils dépassés." },
          { word: 'Dette nette', def: 'Dettes financières − trésorerie. Mesure le vrai endettement.' },
          { word: 'Mandataire judiciaire', def: 'Officier ministériel qui pilote une liquidation ou un redressement. Vend les actifs pour payer les créanciers.' },
          { word: 'Pacte d\'actionnaires', def: 'Accord privé entre associés sur la gouvernance, les sorties, les droits de vote.' },
          { word: 'Share deal', def: 'Rachat des actions/parts sociales. Reprise complète, fiscalité spécifique sur la plus-value.' },
          { word: 'Synergie', def: "Économie ou gain de CA généré par l'union (achats groupés, contrats croisés, optimisation flotte)." },
        ],
      },
      {
        type: 'diagram',
        title: 'Le funnel M&A',
        intro: "Sur 100 cibles identifiées, seules quelques unes finissent en closing. La majorité du travail est de filtrer rapidement les dossiers faibles.",
        diagram: 'ma-funnel',
        legend: [
          { label: 'Cibles',     desc: '100 entreprises sourcées par segment / géographie.' },
          { label: 'NDA',        desc: '~30 acceptent un accord de confidentialité pour partager des chiffres.' },
          { label: 'Due dil.',   desc: '~10 passent l\'audit financier et opérationnel.' },
          { label: 'LOI',        desc: '~3-4 signent une lettre d\'intention avec exclusivité.' },
          { label: 'SPA',        desc: '~1-2 négocient un Sale & Purchase Agreement définitif.' },
          { label: 'Closing',    desc: '1 closing effectif chez le notaire.' },
        ],
      },
      {
        type: 'cases',
        title: 'Cas pratiques',
        items: [
          {
            title: 'Première offre — combien proposer ?',
            situation: "Cible : 8 camions, CA 720 k€/an, EBE 60 k€, dette nette 80 k€. Vendeur demande 1,2 M€.",
            solution: "Méthode multiple EBE (transport : 4-6×) → 60 × 5 = 300 k€ + dette nette à reprendre = 380 k€. Méthode SCD → ~ 800 k€ (voir formule). Faites une offre de 600 k€ avec earn-out 200 k€ conditionné à la rétention chauffeurs > 80 % sur 18 mois. Vendeur perçoit 800 k€ « possible » mais vous limitez le risque.",
            lesson: "Ne payez JAMAIS comptant ce qu'un earn-out peut financer. L'earn-out aligne le vendeur sur la transition.",
          },
          {
            title: 'Garder les chauffeurs après rachat',
            situation: "Vous rachetez une PME de 12 chauffeurs. Satisfaction moyenne : 55. Votre réputation : 70.",
            solution: "Modifier rétention = +6 %. Base sat 55 (zone 40-60) = 60 % de rétention naturelle = 66 % après modif. Soit ~8 chauffeurs gardés sur 12. Action : entretien individuel avec chacun dans les 15 jours + prime de fusion (~ 500 €) pour les seniors stratégiques.",
            lesson: "Le pire scénario d'une acquisition : payer la cible et perdre les chauffeurs en 3 mois. Investissez 5-10 k€ en prime rétention systématiquement.",
          },
          {
            title: 'Antitrust — quand s\'inquiéter',
            situation: "Vous rachetez le n°3 régional. Vous étiez n°2. Marché local Lyon = 4 M€. Vous = 1,2 M€. Cible = 0,8 M€.",
            solution: "PDM combinée = (1,2 + 0,8) / 4 = 50 %. Au-delà de 30 %, l'Autorité de la Concurrence examine. Notification ANTICOMP obligatoire si le seuil de notification est dépassé. Préparez un dossier de cessions de contrats partielles à un tiers pour redescendre à ~40 %.",
            lesson: "Anticipez l'antitrust dès la due dil. : ce n'est pas une formalité, ça peut faire échouer le deal en dernière minute.",
          },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Pièges à éviter',
        items: [
          {
            title: 'Payer comptant un goodwill élevé',
            mistake: "Vous payez 600 k€ une boîte qui a 200 k€ d'actifs nets. Le goodwill (400 k€) doit être amorti sur 10 ans → 40 k€/an de charge qui plombe le compte de résultat.",
            signal: "Goodwill > 50 % du prix d'achat = signal de surpayement.",
            fix: "Justifiez chaque euro de goodwill par une synergie chiffrée (contrats, équipe, brevet, marque). Sinon, négociez vers le bas.",
          },
          {
            title: 'Ignorer les engagements hors bilan',
            mistake: "Vous rachetez une boîte qui a des leasings cachés. 6 mois après, vous découvrez 240 k€ d'engagements à payer sur 3 ans.",
            signal: "Comptes de classe 8 (engagements) absents ou minces dans la liasse fiscale = drapeau rouge.",
            fix: "Exigez la liste exhaustive des contrats de crédit-bail, locations, garanties données AVANT la LOI. Insérez une clause de représentation & garantie pour les passifs cachés.",
          },
          {
            title: 'Négliger l\'intégration culturelle',
            mistake: "Vous fusionnez deux équipes sans réunion. Les chauffeurs des deux camps s'ignorent ou se méfient. Climat social en chute libre.",
            signal: "Démissions x2 dans les 90 jours post-closing + messagerie pleine de plaintes croisées.",
            fix: "Plan d'intégration sur 100 jours : journée de team building en J+30, badges/uniformes unifiés en J+60, comité mixte en J+90. Budget 5-10 k€ qui en vaut 50.\n\n**Astuce SCD** : pour chaque chauffeur acquis lors d'une fusion, consultez sa **carte de profil enrichie** (photo + bio + archétype de personnalité — vétéran, jeune motivé, méthodique, etc.). Le badge **M&A** apparaît sur la carte. Calez vos premiers entretiens sur ces traits — un « vétéran old school » ne réagit pas comme un « jeune motivé ». 80 % de la rétention se joue dans le ton du premier échange.",
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 7. COMMERCIAL & VENTE
  // (Prospection, MEDDIC, détection AO, supports back-office) — 04/06/2026
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'commercial',
    title: 'Commercial & Vente',
    subtitle: 'Prospection, MEDDIC, appels d\'offres, supports BO',
    icon: 'Briefcase',
    color: '#F59E0B',
    pages: [
      {
        type: 'cover',
        title: 'Commercial & Vente',
        intro: "Sans nouveaux clients, votre carnet d'ordre s'érode mécaniquement. La fonction commerciale n'est pas un coût : c'est le moteur qui alimente toute la machine — flotte, RH, finance. Apprenez à prospecter, qualifier et argumenter méthodiquement.",
        bullets: [
          'Cycle de vente B2B et méthode MEDDIC',
          'Prospection : sources, canaux, ratios',
          'Détection et qualification d\'appels d\'offres',
          'Supports back-office : qui fait quoi',
        ],
      },
      {
        type: 'definitions',
        title: 'Concepts clés',
        items: [
          { term: 'Pipeline commercial',
            def: 'Vue agrégée des opportunités en cours, classées par étape (lead → contact → devis → négo → signature). Un pipeline sain présente un ratio 3-5× le CA cible (taux de transfo ~20-30 %).' },
          { term: 'Lead',
            def: 'Contact identifié, dont les coordonnées et le besoin sont qualifiés. Distingue-toi de la "piste" (juste un nom, pas encore qualifié).' },
          { term: 'MEDDIC',
            def: 'Méthode de qualification B2B : Metrics, Economic buyer, Decision criteria, Decision process, Identify pain, Champion. Chaque lettre = une question pour ne pas perdre 3 mois sur une opportunité morte.' },
          { term: 'Tunnel de vente',
            def: 'Représentation visuelle du parcours prospect → client. Chaque étape a un taux de conversion : par ex. 100 leads → 30 RDV → 10 devis → 3 signatures = 3 % global.' },
          { term: 'Cycle de vente',
            def: 'Durée moyenne entre premier contact et signature. En transport B2B PME : 4-8 semaines. Grands comptes / AO publics : 3-9 mois.' },
          { term: 'Appel d\'offres (AO)',
            def: 'Procédure formalisée où un acheteur (privé ou public) sollicite plusieurs prestataires sur un cahier des charges précis. Marché privé négocié OU marché public encadré (BOAMP, JOUE).' },
          { term: 'BOAMP / JOUE',
            def: 'Bulletin Officiel des Annonces de Marchés Publics (France) et Journal Officiel de l\'Union Européenne. Plateformes obligatoires de publication des AO publics au-dessus de seuils.' },
          { term: 'Mémoire technique',
            def: 'Document central d\'une réponse à AO décrivant moyens, organisation, qualité, RSE. Représente souvent 40-60 % de la note finale (vs prix 30-40 %).' },
        ],
      },
      {
        type: 'formulas',
        title: 'Cycle de vente B2B en transport',
        items: [
          {
            name: 'Étape 1 — Prospection',
            formula: 'Sources : LinkedIn, salons, recommandation, BOAMP, fichiers CCI',
            example: 'En transport régional : viser les directeurs logistiques et acheteurs des secteurs cible (BTP, agroalim, retail). Volume mensuel typique : 50 contacts froids = 5 RDV qualifiés.',
          },
          {
            name: 'Étape 2 — Qualification (MEDDIC)',
            formula: 'Metrics : volume, fréquence, distance. Economic buyer : qui signe ? Decision criteria : prix / délais / qualité / RSE. Decision process : appel d\'offres ou gré à gré ? Identify pain : qu\'est-ce qui ne marche pas avec leur transporteur actuel ?',
            example: 'Si tu ne peux pas répondre aux 6 lettres MEDDIC après 2 RDV, l\'opportunité n\'est pas mûre. Range-la et reviens dans 3 mois.',
          },
          {
            name: 'Étape 3 — Devis & négociation',
            formula: 'Tarif = (coût direct × 1,35) + marge cible. Argumenter sur le critère principal du client (cf. mini-jeu d\'argumentation).',
            example: 'PME industrielle : sensible au prix. Public/Santé : sensible à la qualité documentée. Export : sensible aux délais.',
          },
          {
            name: 'Étape 4 — Closing',
            formula: 'Lever les 3 derniers freins : prix, délais de paiement, références. Proposer un contrat-pilote 3 mois si méfiance.',
            example: '85 % des deals perdus le sont par manque de relance entre RDV. Relancer J+3, J+7, J+15.',
          },
        ],
      },
      {
        type: 'formulas',
        title: 'Détection d\'appels d\'offres',
        items: [
          {
            name: 'Sources publiques (gratuites)',
            formula: 'BOAMP.fr (national) · marches-publics.gouv.fr · JOUE (européen) · plateformes régionales (PLACE, AWS)',
            example: 'Marché transport > 40 000 € HT publié obligatoirement au BOAMP. Filtrer par mot-clé : "transport routier", "messagerie", "logistique", "frigorifique".',
          },
          {
            name: 'Sources privées (abonnement)',
            formula: 'Vecteur Plus, Doubletrade, ProDouane — alertes automatisées sur les profils-cible',
            example: 'Investissement 100-300 €/mois selon couverture. Rentable dès le 1er AO remporté (~50-200 k€ contrat).',
          },
          {
            name: 'Sources informelles',
            formula: 'Réseau, LinkedIn, salons (SITL Paris, Tranzit, IT-Trans). Veille concurrence : qui perd un client = opportunité',
            example: 'Un commercial expérimenté capte 30-40 % de ses AO en off-market via son réseau.',
          },
          {
            name: 'Qualifier rapidement (en 15 minutes)',
            formula: '1. Volume vs ma capacité — 2. CCAP / pénalités acceptables ? — 3. Date limite réaliste ? — 4. Ai-je les références ? — 5. Vais-je gagner ma marge ?',
            example: 'Si une seule réponse est "non" : pas la peine de soumissionner. La sélection des AO est aussi importante que la qualité de la réponse.',
          },
        ],
      },
      {
        type: 'definitions',
        title: 'Supports back-office commercial',
        items: [
          { term: 'Rémi Forni — Marketing',
            def: 'Génère leads via campagnes ciblées et présence sur les salons. +1 à +2 pts de notoriété/mois, +2 contrats simultanés visibles. Indispensable au démarrage Phase 2.' },
          { term: 'Sherazade Fetitou — Communication',
            def: 'Bâtit la marque employeur + réputation client (+1 contrat). Effet de long-terme sur LTV et taux de transformation.' },
          { term: 'Léa Bertrand — Commercial junior',
            def: 'Prospection terrain + qualification téléphonique. +2 contrats visibles, débloque le skip du mini-jeu d\'argumentation. Premier commercial à recruter dès >5 k€/mois.' },
          { term: 'Marc Dupuis — Commercial senior',
            def: 'Gestion grands comptes + réponses AO complexes. +3 contrats visibles. À recruter quand un commercial junior plafonne (vers 15-25 k€/mois).' },
          { term: 'Sylvie Marchand — Commercial expert',
            def: 'Closing top-tier + négociation Tier 4-5. Débloque l\'accès aux contrats premium internationaux. Recrutement Phase 3-4.' },
          { term: 'Mémoire technique (rédaction)',
            def: 'Pour un AO complexe (> 200 k€), la rédaction du mémoire technique peut prendre 5-10 jours-homme. Marc ou Sylvie l\'absorbent en arrière-plan ; sans eux, vous le faites vous-même.' },
          { term: 'CRM (outil)',
            def: 'Customer Relationship Management — base centrale de toutes les interactions clients. Sans CRM : 30 % des opportunités tombent dans les oubliettes. Investissement à débloquer dès 5-10 clients actifs.' },
        ],
      },
      {
        type: 'formulas',
        title: 'Indicateurs commerciaux à suivre',
        items: [
          {
            name: 'Taux de transformation',
            formula: 'Signatures ÷ Devis envoyés × 100',
            example: 'En transport B2B : 25-35 % est bon, > 40 % suggère que vous bridez votre pipeline (devis trop sélectifs ou prix trop bas).',
          },
          {
            name: 'Coût d\'acquisition client (CAC)',
            formula: '(Marketing + Salaires commerciaux + Frais salons) ÷ Nouveaux clients signés',
            example: 'Cible : CAC ≤ 15-20 % du CA annuel généré par le client. Si CAC = 8 k€ et client = 30 k€/an : ratio sain (1:3,75).',
          },
          {
            name: 'Pipeline coverage',
            formula: 'CA prévisionnel du pipeline ÷ Objectif CA',
            example: 'Coverage sain : 3-5× l\'objectif (pour absorber les 70-80 % d\'opportunités perdues). Si coverage < 2× : alerte rouge, accélérer la prospection.',
          },
          {
            name: 'Net Promoter Score (NPS)',
            formula: 'Promoteurs (9-10) - Détracteurs (0-6), sur 100',
            example: 'NPS > +30 = excellent en B2B. Mesuré sur enquête trimestrielle. Booste passivement le bouche-à-oreille (= notoriété en jeu).',
          },
        ],
      },
      {
        type: 'definitions',
        title: 'Erreurs à éviter',
        items: [
          { term: 'Le "tunnel d\'engagement"',
            def: 'Continuer à investir temps et énergie sur un prospect chaud qui ne décide pas. Si > 4 mois sans avancée tangible : closer ou archiver.' },
          { term: 'Le devis "spray and pray"',
            def: 'Envoyer 50 devis sans qualification préalable. Résultat : 0 signature et un taux de transfo ridicule. Qualifier AVANT de chiffrer.' },
          { term: 'Le prix-d\'appel suicidaire',
            def: 'Casser les prix pour décrocher un premier contrat. Conséquence : marge négative, escalade des exigences, départ du client dès qu\'un moins cher arrive. Préférez un volume réduit à prix juste.' },
          { term: 'Ignorer les signaux faibles',
            def: 'Une PME en croissance, un changement d\'acheteur, un dépôt qui s\'ouvre = autant de signaux. Le commercial qui voit avant les autres remporte les meilleurs deals.' },
          { term: 'Sous-estimer le mémoire technique',
            def: 'Un AO public se gagne sur la qualité du mémoire (40-60 % de la note). Bâcler la rédaction = perdre des points faciles à un concurrent qui a soigné le sien.' },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Vocabulaire',
        items: [
          { word: 'Lead qualifié', def: 'Contact dont le besoin, le budget et la décision-maker sont identifiés (MQL = Marketing Qualified Lead, SQL = Sales Qualified Lead).' },
          { word: 'Cold call', def: 'Appel à un prospect sans contact préalable. Taux moyen B2B : 1-3 % de RDV obtenu.' },
          { word: 'Closing', def: 'Étape finale de signature. Techniques : urgence, social proof, alternative, silence.' },
          { word: 'Up-sell / Cross-sell', def: 'Vendre une version premium (up) ou un service complémentaire (cross) à un client existant. Coût d\'acquisition divisé par 5 vs nouveau client.' },
          { word: 'CCAP / CCTP', def: 'Cahier des Clauses Administratives et Techniques Particulières — documents contractuels d\'un AO public, à lire intégralement avant de soumissionner.' },
          { word: 'OPEX vs CAPEX', def: 'Opex = dépenses opérationnelles (carburant, RH). Capex = investissements (véhicules, dépôts). Un acheteur arbitre souvent entre les deux.' },
          { word: 'CRM', def: 'Customer Relationship Management. Logiciel ou méthodologie pour centraliser les interactions clients.' },
        ],
      },
    ],
  },


  // ════════════════════════════════════════════════════════════════════
  // 7. CLIENTS & FIDÉLISATION (CRM)
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'clients',
    title: 'Clients & Fidélisation',
    subtitle: 'CRM, paliers, lifetime value',
    icon: 'UserCircle',
    color: '#0EA5E9',
    pages: [
      {
        type: 'cover',
        title: 'Clients & Fidélisation',
        intro: "Conquérir un client coûte 5× plus cher que de le garder. Un Partenaire fidèle vous recommande, accepte vos prix, et passe sur vos petits couacs. Cultiver le portefeuille = effet boule de neige.",
        bullets: [
          '5 paliers de relation : Méfiant → Partenaire',
          'Actions qui font grimper / chuter le score',
          'Lifetime Value (LTV) d\'un client',
        ],
      },
      {
        type: 'definitions',
        title: 'Définitions essentielles',
        items: [
          { term: 'Score de fidélité (0-100)', def: 'Mesure agrégée de la qualité de la relation client. Évolue avec chaque interaction (livraison, retard, annulation, recommandation).' },
          { term: 'Palier Méfiant (0-20)',     def: 'Client en perte de confiance. Cooldown 30 jours pendant lequel il ne propose plus de contrat. Menace de résiliation.' },
          { term: 'Palier Neutre (20-40)',     def: 'Démarrage de relation. Standards du marché, pas d\'avantage particulier.' },
          { term: 'Palier Confiant (40-65)',   def: 'Confiance établie. Renouvellements simples, communication détendue.' },
          { term: 'Palier Fidèle (65-85)',     def: 'Préférence affichée. Vous obtenez des missions sup. à -2 % de remise consentie (perception « partenaire de confiance »).' },
          { term: 'Palier Partenaire (85-100)',def: 'Top of mind. Le client vous recommande, vous invite à ses événements, paie sans négocier.' },
          { term: 'Lifetime Value (LTV)',      def: 'Revenu total prévisible sur la durée de relation. Un Fidèle vaut ~5× un Neutre sur 3 ans.' },
          { term: 'Cooldown Méfiant',          def: 'Délai d\'apaisement (30 jours) automatique après chute en Méfiant. Le client se ferme à toute nouvelle proposition.' },
        ],
      },
      {
        type: 'formulas',
        title: 'Formules de calcul',
        items: [
          {
            name: 'Évolution du score (par événement)',
            formula: '+2 livraison à temps · +1 livraison hebdo en cours · −4 retard · −15 annulation contrat · +10 année sans incident · +0,5 réputation > 70',
            example: "Un contrat de 30 j livré sans accroc : 4 × (+1 hebdo) + 1 × (+2 fin) = +6 pts. Une annulation efface ~15 livraisons hebdo de boulot.",
          },
          {
            name: 'Multiplicateur de prix par palier',
            formula: 'Méfiant ×1,0 — Neutre ×1,0 — Confiant ×1,0 — Fidèle ×0,98 — Partenaire ×0,98',
            example: "Un client Fidèle paye -2 % vs liste. Compensé largement par sa LTV (volume × durée).",
          },
          {
            name: 'Lifetime Value estimée',
            formula: 'LTV ≈ revenu_mensuel × durée_relation_mois × probabilité_renouvellement',
            example: "Confiant : 2 000 €/mo × 24 mois × 0,75 = 36 000 €. Partenaire : 2 000 × 60 × 0,95 = 114 000 €. Soit ×3,2.",
          },
          {
            name: 'Coût d\'une perte de client',
            formula: 'Coût = LTV restante + coût d\'acquisition d\'un remplaçant (typ. 800-2 000 €)',
            example: "Perte d'un Fidèle à 40 k€ de LTV restant = perte directe + 1 500 € pour acquérir un remplaçant Neutre qui mettra 2 ans à atteindre Fidèle.",
          },
        ],
      },
      {
        type: 'diagram',
        title: 'Les 5 paliers de fidélité',
        intro: "Chaque palier déverrouille des comportements différents : un Partenaire vous recommande, un Méfiant menace de résilier.",
        diagram: 'client-tiers',
        legend: [
          { label: 'Méfiant',     desc: 'Mise en demeure, menace, ne propose plus de contrats.',          color: '#DC2626' },
          { label: 'Neutre',      desc: 'Standard, aucune émotion particulière.',                        color: '#9CA3AF' },
          { label: 'Confiant',    desc: 'Renouvellements faciles, remerciements ponctuels.',             color: '#0891B2' },
          { label: 'Fidèle',      desc: 'Préférence affichée, propose des missions extra.',              color: '#D97706' },
          { label: 'Partenaire',  desc: 'Recommandations, cadeaux, invitations VIP.',                    color: '#10B981' },
        ],
      },
      {
        type: 'cases',
        title: 'Cas pratiques',
        items: [
          {
            title: 'Rattraper un client Méfiant',
            situation: "TransLog SA est tombé à 12/100 (Méfiant) suite à 2 retards consécutifs. Le cooldown 30 jours bloque les nouveaux contrats.",
            solution: "Étape 1 : écrire un mail d'excuse personnalisé (template `cli-mefiant-complaint-01` choix `apologise`) + offrir un avoir 300 €. Étape 2 : sur le contrat en cours, mettez votre meilleur chauffeur. Étape 3 : attendez la fin du cooldown puis proposez un contrat à -5 % pour redémarrer la confiance. ~60 jours pour remonter à Neutre.",
            lesson: "Un Méfiant n'est pas perdu. Il faut une action concrète et visible. Investissez 500-1 000 € pour sauver une LTV de 20 k€.",
          },
          {
            title: 'Stratégie de portefeuille équilibré',
            situation: "Vous avez 10 clients actifs : 6 Neutres, 2 Confiants, 1 Fidèle, 1 Méfiant. Votre CA dépend à 50 % du Fidèle.",
            solution: "Risque concentré : si le Fidèle part, -50 % de CA d'un coup. Action : faire monter 2-3 Confiants en Fidèle en parallèle. Plan : qualité irréprochable + un cadeau symbolique (carton de Bordeaux mis en valeur) en année 1 + extra-mission proposée systématiquement.",
            lesson: "Visez 3 Fidèles minimum dans le portefeuille. Aucun client ne doit représenter > 25 % du CA total.",
          },
          {
            title: "Refuser un contrat sans froisser",
            situation: "Marie's Cuisine (Confiant, 55) vous propose une mission ponctuelle. Votre flotte est saturée. Refuser = -3 pts.",
            solution: "Au lieu d'un refus sec : recommandez un confrère de confiance (qui vous renverra l'ascenseur). Coût : 0 €. Bénéfice : le score baisse de seulement -1 au lieu de -3, et le client perçoit votre honnêteté.",
            lesson: "Un « non » habile vaut un « oui ». Construire un réseau de transporteurs partenaires permet de gérer les saturations sans casser la relation.",
          },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Pièges à éviter',
        items: [
          {
            title: 'Tout miser sur 1 gros compte',
            mistake: "Le grand compte représente 60 % de votre CA. Vous adaptez tout à ses exigences.",
            signal: 'Top 1 client > 30 % du CA mensuel sur 3 mois consécutifs.',
            fix: "Dès qu'un client passe 25 % du CA, lancez une prospection active sur 2-3 nouveaux segments. Diversifiez avant qu'il ne le décide pour vous.",
          },
          {
            title: 'Brader pour récupérer un Méfiant',
            mistake: "Pour calmer un Méfiant, vous offrez -20 % sur le prochain contrat. Vous perdez la marge sans rétablir la confiance (le service n'a pas changé).",
            signal: "Remise > 10 % sur un contrat existant = pansement, pas remède.",
            fix: "La remise N'EST PAS le levier de retour de confiance. Engagez-vous sur un INDICATEUR vérifiable (0 retard sur 30 jours, chauffeur dédié, rapport hebdo). Ça coûte moins cher et reconstruit vraiment.",
          },
          {
            title: 'Négliger les Confiants au profit des Fidèles',
            mistake: "Vous chouchoutez vos 2 Fidèles et oubliez vos 5 Confiants. 6 mois plus tard, 3 sont tombés à Neutre.",
            signal: 'Aucun message contextuel envoyé à un client Confiant > 60 jours.',
            fix: "Routine mensuelle : 1 message personnalisé à CHAQUE client Confiant (newsletter, point d'avancement, info marché). Coût 0, retour énorme.",
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Vocabulaire',
        items: [
          { word: 'Account manager',         def: 'Personne en charge du suivi d\'un client clé. Pour une PME : c\'est le patron au début.' },
          { word: 'Churn',                   def: "Taux d'attrition. % de clients perdus sur une période donnée." },
          { word: 'Cross-selling',           def: "Vendre un service complémentaire à un client existant (ex : ajouter du frigo à un client messagerie)." },
          { word: 'KPI',                     def: 'Key Performance Indicator. Indicateur clé de pilotage (CA mensuel, NPS, churn, LTV).' },
          { word: 'NPS',                     def: 'Net Promoter Score. Mesure de recommandation (de 0 à 100). > 50 = excellent.' },
          { word: 'Onboarding',              def: 'Phase d\'intégration d\'un nouveau client. Critique : 80 % de l\'opinion long terme se forme dans les 90 premiers jours.' },
          { word: 'RFP',                     def: 'Request For Proposal — appel d\'offres formalisé. Vous remettez une offre détaillée écrite.' },
          { word: 'Up-selling',              def: 'Faire monter un client en gamme (ex : passer d\'un contrat 30 j à un contrat 90 j cadré).' },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 8. APPELS D'OFFRES (AO) — 02/06/2026
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'appels-offres',
    title: 'Appels d\'Offres',
    subtitle: 'Marchés long-terme, soumission, négociation',
    icon: 'FileSignature',
    color: '#10B981',
    pages: [
      {
        type: 'cover',
        title: 'Appels d\'Offres en France',
        intro: "Un appel d'offres (AO) est un processus formel par lequel un acheteur (entreprise ou administration) met en concurrence plusieurs prestataires sur un marché long-terme. C'est la voie d'accès aux gros contrats (collectivités, grands comptes, marchés publics). Bien préparer = remporter des engagements de 6 à 18 mois qui sécurisent votre carnet.",
        bullets: [
          '3 niveaux : local (mairies, PME) → régional (coopératives, ETI) → national (grands comptes, marchés publics)',
          'Critères multiples : prix, RSE, fiabilité financière, références',
          'Phase de négociation possible (contre-offre client)',
          'Engagement de durée fixe imposé par le client (de 90 j à 540 j)',
        ],
      },
      {
        type: 'definitions',
        title: 'Définitions essentielles',
        items: [
          { term: 'AO (Appel d\'Offres)',     def: 'Procédure formelle de mise en concurrence. Le donneur d\'ordres (acheteur) publie un cahier des charges, les prestataires soumissionnent, l\'acheteur compare et tranche.' },
          { term: 'Cahier des charges',       def: 'Document détaillant les attentes : périmètre, volumes, contraintes qualité/HSE/RSE, critères de notation, durée d\'engagement, modalités de prix.' },
          { term: 'Marché public',            def: 'AO d\'une administration (mairie, région, État, hôpitaux, SNCF…). Régi par le Code de la Commande Publique. Critères pondérés (prix + technique + RSE) obligatoires au-delà de 40 k€.' },
          { term: 'Marché privé',             def: 'AO d\'une entreprise privée. Plus libre dans la forme, mais la concurrence reste vive (souvent 3-5 prestataires en lice).' },
          { term: 'BAFO (Best And Final Offer)', def: 'Dernière itération de négociation : l\'acheteur demande à ses 2-3 finalistes leur meilleure offre définitive. Phase typique sur les marchés > 100 k€.' },
          { term: 'Critères pondérés',        def: 'Système de notation transparente : ex. prix 40 % / RSE 30 % / technique 30 %. Chaque critère donne une note, somme pondérée = note finale.' },
          { term: 'EcoVadis',                 def: 'Référentiel international de notation RSE (Bronze → Silver → Gold → Platinum). De plus en plus exigé par les grands comptes en condition d\'éligibilité.' },
          { term: 'Référencement',            def: 'Statut « prestataire validé » chez un grand compte après un AO gagné. Ouvre des marchés complémentaires sans repasser par AO complet.' },
          { term: 'Contre-proposition',       def: 'L\'acheteur revient avec un prix inférieur à votre offre (souvent -5 à -10 %). Vous arbitrez : accepter, refuser (et perdre), re-négocier.' },
        ],
      },
      {
        type: 'formulas',
        title: 'Formules de scoring',
        items: [
          {
            name: 'Probabilité d\'obtention SCD (modèle simplifié)',
            formula: 'P = 0,50 + ΔRép + ΔPrix + ΔRSE + ΔFlotte + ΔFin + ΔHSE',
            example: 'Bornée entre 5 % et 95 %. ΔPrix = ±20 (offre à -20 % réf = +0,20 ; offre +30 % = -0,20). ΔRép = ±18 (réputation × notoriété). ΔRSE = +15 max selon score RSE + tier EcoVadis. ΔHSE = +7 ou +15 selon engagement modéré ou fort.',
          },
          {
            name: 'Note finale marché public type',
            formula: '0,4 × note_prix + 0,3 × note_technique + 0,3 × note_RSE',
            example: 'Prestataire A : prix 18/20 + tech 16/20 + RSE 14/20 = 16,4. Prestataire B : prix 14/20 + tech 18/20 + RSE 18/20 = 16,4 (équivalent). Le prix seul ne suffit plus.',
          },
          {
            name: 'CA sécurisé par un AO',
            formula: 'Prix/jour × Durée fixée par le client (jours)',
            example: 'AO MecAero : 340 €/j × 270 j = 91 800 € de CA garanti sur 9 mois. Marge à modéliser après coûts opérationnels (~30-35 % en moyenne).',
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Vocabulaire AO',
        items: [
          { word: 'AO',                       def: 'Appel d\'Offres. Voir aussi : RFP (anglais).' },
          { word: 'AO restreint',             def: 'Marché où seules les entreprises pré-sélectionnées peuvent soumissionner (vs. AO ouvert où tout le monde peut candidater).' },
          { word: 'CCAP',                     def: 'Cahier des Clauses Administratives Particulières. Document juridique annexé au marché public.' },
          { word: 'CCTP',                     def: 'Cahier des Clauses Techniques Particulières. Décrit les prestations attendues sur le plan technique.' },
          { word: 'DCE',                      def: 'Dossier de Consultation des Entreprises. Pack complet remis aux candidats : règlement, CCAP, CCTP, BPU.' },
          { word: 'BPU',                      def: 'Bordereau des Prix Unitaires. Tableau prix poste par poste, base de comparaison entre candidats.' },
          { word: 'DUME',                     def: 'Document Unique de Marché Européen. Déclaration sur l\'honneur d\'éligibilité à un marché public.' },
          { word: 'Notation transparente',    def: 'Obligation pour l\'acheteur public de publier la grille de notation avant les soumissions et d\'expliquer les décisions a posteriori.' },
          { word: 'Recours Précontractuel',   def: 'Action juridique permettant à un candidat évincé de contester l\'attribution du marché public (Tribunal Administratif).' },
        ],
      },
      {
        type: 'concept',
        title: 'Les 3 phases d\'un AO',
        sections: [
          {
            title: '① Publication & dossier (J-30 à J0)',
            content: "L'acheteur publie le DCE (Dossier de Consultation des Entreprises). Vous lisez le cahier des charges, vérifiez votre éligibilité (CA, références, certifications) et préparez votre offre. Dans SCD, c'est l'écran « Appels d'Offres » : 3 cards visibles avec leur narratif, leur durée fixe et leurs prérequis (flotte mini, RSE mini, % médical mini).",
          },
          {
            title: '② Soumission & analyse (J0 à J+8/14)',
            content: "Vous soumettez votre offre (prix /jour + engagement HSE). L'acheteur compare les candidats sur les critères pondérés. Dans SCD, la probabilité d'obtention est calculée live à partir de 6 facteurs et roll à J+8 à J+14. Si l'acheteur est intéressé mais hésitant, il revient avec une contre-proposition à -8 %.",
          },
          {
            title: '③ Attribution & exécution',
            content: "L'acheteur tranche : marché remporté (signature contrat ferme), refusé (vous repartez), ou phase BAFO sur les très gros marchés. Dans SCD, un AO gagné devient un contrat « régulier » dans le Marché : il vous reste à l'affecter à un véhicule + chauffeur compatible pour démarrer la prestation.",
          },
        ],
      },
      {
        type: 'cases',
        title: 'Cas pratiques',
        items: [
          {
            title: 'Faut-il candidater à TOUS les AO ?',
            situation: "Votre carnet est plein à 70 %. Marc Dupuis vous remonte 3 AO simultanément : Mairie de Tours (local), Coopérative Aquitaine (régional), MecAero (régional). Vous ne pouvez en exécuter que 2 max.",
            solution: "Non — la dispersion tue le taux de réussite ET la qualité d'exécution. Stratégie gagnante : prioriser 1-2 AO par mois en alignement avec vos prérequis (flotte, RSE, chauffeurs disponibles). Refuser un AO ne pénalise pas (vs. signer puis sous-exécuter qui chute votre réputation de 5-10 points).",
            lesson: "Un AO bien préparé vaut 3 AO bâclés. Concentrez la fonction commerciale sur peu de dossiers très ciblés.",
          },
          {
            title: 'Prix bas vs. engagement HSE fort',
            situation: "Vous candidatez sur Bio&Frais Coopérative (245 €/j de réf, durée 180 j). Choix : (A) prix 220 €/j + HSE standard, ou (B) prix 250 €/j + engagement HSE fort (audit trimestriel + flotte verte 25 %).",
            solution: "L'option B est généralement gagnante sur les clients RSE-sensibles. ΔPrix de A vs B = -0,12 ; mais ΔHSE = +0,15. Net +0,03 sur la probabilité. ET vous évitez le piège du dumping qui vous enferme dans un positionnement bas-de-gamme. À l'inverse, sur Bricoland (distribution low-cost), c'est le prix qui prime.",
            lesson: "Adaptez votre stratégie au profil client : RSE-sensible → engagement HSE fort ; low-cost → prix agressif. Pas de recette unique.",
          },
          {
            title: 'Accepter ou refuser une contre-proposition ?',
            situation: "Vous avez soumis MecAero à 340 €/j × 270 j (91 800 €). Le client revient avec 313 €/j (84 510 €). Soit -8 %. Marge initiale prévue : 32 % ; à 313 €/j, marge ~25 %.",
            solution: "Acceptez si la marge reste > 20 % ET que c'est votre première référence Aéro (effet portfolio). Refusez si la marge tombe < 15 % OU si vous avez d'autres AO en parallèle plus lucratifs. Règle de pouce : un contrat qui finance vos coûts fixes + dégage > 15 % de marge = bon à signer.",
            lesson: "Une contre-proposition est un signal d'intérêt fort du client. Refuser sans alternative = perte sèche. Acceptez si la marge reste raisonnable.",
          },
        ],
      },
      {
        type: 'tips',
        title: 'Pièges à éviter',
        items: [
          {
            title: 'Confondre durée souhaitée et durée imposée',
            warning: "La durée affichée dans SCD est IMPOSÉE par le client. Pas de slider de durée — c'est volontaire et réaliste : un acheteur public impose la durée d'un marché à 12 ou 24 mois, ce n'est pas négociable.",
          },
          {
            title: 'Sous-estimer le coût de production de l\'offre',
            warning: "Préparer un dossier marché public sérieux coûte 2-5 jours-personne (estimation, références, plan HSE, dossier administratif). Soumissionner partout = budget commercial à plein régime. Mieux vaut 5 candidatures travaillées que 15 à la chaîne.",
          },
          {
            title: 'Croire que le prix le plus bas gagne TOUJOURS',
            warning: "Faux sur les marchés > 100 k€ et tous les marchés publics. La grille de notation pondère prix/technique/RSE. Une offre 10 % plus chère mais Gold EcoVadis peut battre un concurrent low-cost sans RSE.",
          },
          {
            title: 'Oublier l\'effet cliquet RSE',
            warning: "Un AO Carrefour exige EcoVadis Silver mini. Si votre tier n'est pas atteint, l'AO est INVISIBLE sur votre board — pas même une chance de soumissionner. Investir dans Claire Maréchal + démarche RSE = condition d'accès, pas option.",
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 9. DROIT — Cadre juridique de l'entreprise de transport
  // 17/06/2026 (Lot 6 Phase L3) — Chapitre dédié couvrant droit des
  // sociétés, droit du travail, concurrence, fiscal contentieux,
  // international du transport (CMR/Incoterms/douanes), responsabilité
  // du dirigeant. Complète le chapitre 1 « Fiscalité & Statuts » qui
  // reste centré sur le quotidien fiscal (TVA, IS, statuts).
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'droit',
    title: 'Droit & Procédures',
    subtitle: 'Sociétés, travail, concurrence, international',
    icon: 'Scale',
    color: '#92400E',
    pages: [
      {
        type: 'cover',
        title: 'Droit & Procédures',
        intro: "Diriger une entreprise de transport, c'est naviguer dans 6 branches du droit qui peuvent à tout moment vous convoquer ou vous sanctionner. Connaître le cadre — sans devenir avocat — permet d'éviter les pièges les plus coûteux et de savoir quand sortir le chéquier ou monter au combat.",
        bullets: [
          'Droit des sociétés — gouvernance, capital, responsabilité du dirigeant',
          "Droit du travail — CDI/CDD, rupture, prud'hommes, convention 3085 transport",
          "Droit de la concurrence — entente, abus de position, concurrence déloyale",
          'Droit fiscal contentieux — contrôles URSSAF, DREAL, redressement',
          'Droit international du transport — CMR, Incoterms, douanes EU',
          'Responsabilité civile et pénale du dirigeant',
        ],
      },

      // ── 1. Droit des sociétés ──────────────────────────────────────
      {
        type: 'definitions',
        title: '1. Droit des sociétés',
        items: [
          { term: 'Capital social', def: 'Apport initial des associés. EURL/SARL : 1 € symbolique possible. SAS : 1 € possible mais 5-10 k€ recommandés pour la crédibilité bancaire.' },
          { term: 'Pacte d\'associés', def: "Document confidentiel qui complète les statuts et organise l'entrée/sortie des associés, le droit de préemption, les clauses anti-dilution. Capital outil de gouvernance, pas juste de financement." },
          { term: 'AG ordinaire (AGO)', def: 'Assemblée annuelle obligatoire dans les 6 mois suivant la clôture. Approuve les comptes, donne quitus au dirigeant, affecte le résultat.' },
          { term: 'AG extraordinaire (AGE)', def: 'Modifie les statuts (objet social, capital, forme). Quorum et majorité renforcés. Indispensable pour transformer SARL → SAS, augmenter le capital.' },
          { term: 'Quitus du dirigeant', def: 'Vote en AGO qui décharge le dirigeant de sa responsabilité de gestion sur l\'exercice clos. NB : ne couvre PAS les fautes pénales ni les fraudes.' },
          { term: 'Cessation de paiements', def: 'État caractérisé par l\'impossibilité de faire face au passif exigible avec l\'actif disponible. À déclarer au tribunal sous 45 jours sous peine de faute de gestion.' },
        ],
      },
      {
        type: 'cases',
        title: 'Cas pratique — Augmentation de capital',
        items: [
          {
            title: "Faut-il transformer la SARL en SAS pour lever des fonds ?",
            situation: "Vous gérez une SARL avec un capital de 10 k€. Un investisseur veut entrer pour 100 k€ contre 25 % du capital. Votre comptable suggère de passer en SAS avant l'opération.",
            solution: "La SAS offre une **flexibilité statutaire totale** (clauses d'agrément, dilution contrôlée, actions de préférence). La SARL impose la solidarité fiscale et limite les pactes. **Coût de la transformation : ~2 500 € (frais juridiques + AGE + publication)**. Pour 100 k€ de levée, l'investissement se rentabilise en 0,5 % de la levée.",
            lesson: "Au-delà de 50 k€ de levée, la SAS s'impose. La SARL reste pertinente pour les structures patrimoniales familiales sans ambition de croissance externe.",
          },
          {
            title: "Quitus refusé en AGO — quelles conséquences ?",
            situation: "Vos associés refusent de voter le quitus du dirigeant en AGO suite à un litige sur une décision de gestion (rachat d'un concurrent surévalué).",
            solution: "Le refus de quitus ouvre la porte à une **action sociale en responsabilité** dans les 3 ans. Pour vous protéger : (1) documentez par écrit toutes les décisions stratégiques avant exécution, (2) sollicitez l'avis d'un conseil juridique (Juliette) sur les opérations > 50 k€, (3) demandez une délégation expresse de l'AG pour les actes majeurs.",
            lesson: "Le quitus n'est jamais acquis. Plus vous documentez en amont, moins votre responsabilité est exposée a posteriori.",
          },
        ],
      },

      // ── 2. Droit du travail ────────────────────────────────────────
      {
        type: 'definitions',
        title: '2. Droit du travail',
        items: [
          { term: 'CDI', def: 'Contrat à Durée Indéterminée. Forme normale du contrat de travail. Rupture par démission (salarié), licenciement (employeur) ou rupture conventionnelle (accord).' },
          { term: 'CDD', def: 'Contrat à Durée Déterminée. Cas de recours strictement encadrés (remplacement, accroissement temporaire). Durée max 18 mois renouvellement inclus.' },
          { term: 'Convention collective 3085', def: 'Convention nationale du transport routier de marchandises. Garantit les minima salariaux, primes (douche, repas), indemnités de repos. Application obligatoire dès le 1er chauffeur.' },
          { term: 'Cause réelle et sérieuse', def: 'Motif objectif et vérifiable justifiant un licenciement. À défaut : indemnité minimale 3 à 20 mois de salaire selon ancienneté (barème Macron).' },
          { term: 'Rupture conventionnelle', def: 'Accord amiable entre employeur et salarié. Indemnité au moins égale à l\'indemnité légale de licenciement. Homologation par la DIRECCTE.' },
          { term: "Conseil de Prud'hommes", def: "Juridiction paritaire (employeurs + salariés) compétente pour les litiges du contrat de travail. Phase de conciliation obligatoire avant le jugement contradictoire." },
          { term: 'Inspection du travail', def: "Service de l'État pouvant contrôler à tout moment vos contrats, fiches de paie, durée du travail, sécurité. Pouvoir de mise en demeure et de PV." },
        ],
      },
      {
        type: 'pitfalls',
        title: "Pièges du licenciement en transport",
        items: [
          {
            title: "Motif vague (« inadaptation au poste »)",
            mistake: "Vous licenciez un chauffeur pour « inadaptation au poste » sans préciser les faits, dates, ou inaptitudes médicales.",
            signal: "La lettre de licenciement ne cite aucun fait daté et reproductible.",
            fix: "Chaque licenciement = dossier circonstancié : 3 à 5 faits datés (retards, incidents documentés, rapports clients), avertissements préalables écrits, entretien préalable formalisé. Sans ces 3 éléments, le prud'homme requalifie systématiquement en « sans cause réelle et sérieuse » → 6+ mois de salaire à payer.",
          },
          {
            title: "Oublier la convention collective 3085",
            mistake: "Vous appliquez les minima du Code du travail standard pour vos chauffeurs PL.",
            signal: "Salaire brut inférieur au minimum conventionnel + absence de primes douche / repas / amplitude.",
            fix: "La convention 3085 prime sur le Code du travail. Vérifiez à chaque embauche : coefficient (selon CACES, ADR, expérience), minimum mensuel conventionnel, primes obligatoires. Un seul rappel rétroactif sur 3 ans coûte 8-15 k€ par chauffeur concerné.",
          },
          {
            title: "Procédure expéditive (licenciement < 7 jours)",
            mistake: "Suite à un incident, vous notifiez le licenciement 48 heures après l'entretien préalable.",
            signal: "Délai entre entretien et notification < 2 jours ouvrés.",
            fix: "Le délai de réflexion est de **2 jours ouvrables minimum** entre l'entretien préalable et la notification du licenciement (5 jours pour licenciement disciplinaire). Tout licenciement notifié plus tôt = annulation automatique + 6 mois de salaire d'indemnité.",
          },
        ],
      },

      // ── 3. Droit de la concurrence ────────────────────────────────
      {
        type: 'definitions',
        title: '3. Droit de la concurrence',
        items: [
          { term: 'Entente illicite', def: 'Accord entre concurrents pour fixer les prix, partager les marchés ou limiter la production. Sanctionné jusqu\'à 10 % du CA mondial annuel par l\'Autorité de la concurrence (art. L.420-1 C. com.).' },
          { term: 'Abus de position dominante', def: 'Comportement anti-concurrentiel d\'une entreprise dominante (≥ 40 % de part de marché) : ventes à perte, refus de vente, discrimination tarifaire (art. L.420-2 C. com.).' },
          { term: 'Concurrence déloyale', def: 'Comportement fautif détournant la clientèle d\'un concurrent : dénigrement, parasitisme, confusion de marques, débauchage massif organisé. Sanctionné devant le Tribunal de commerce.' },
          { term: 'Démarchage agressif', def: "Limite : démarchage de clients d'un concurrent EST autorisé tant que (1) pas d'utilisation de fichiers volés, (2) pas de fausses affirmations sur le concurrent, (3) pas de débauchage massif coordonné." },
          { term: 'Clause de non-concurrence', def: "Clause d'un contrat de travail interdisant à un ex-salarié de travailler chez un concurrent. Valable SI : limitée dans le temps + l'espace + métier + contrepartie financière (≥ 30 % du salaire brut)." },
          { term: 'Autorité de la concurrence', def: "Autorité administrative indépendante. Saisine possible par les concurrents, clients, ou auto-saisine. Sanctions financières maxi : 10 % du CA mondial." },
        ],
      },

      // ── 4. Droit fiscal contentieux ────────────────────────────────
      {
        type: 'definitions',
        title: '4. Droit fiscal contentieux',
        items: [
          { term: 'Contrôle sur pièces', def: 'Examen des déclarations à distance par l\'administration fiscale. Pas d\'intervention sur site. Vous ne le verrez pas venir, vous recevez un avis de redressement.' },
          { term: 'Contrôle sur place (vérification)', def: 'Présence d\'un vérificateur dans vos locaux pendant 3 à 6 mois. Examen exhaustif comptabilité + factures + déclarations. Encadré par la Charte du contribuable vérifié.' },
          { term: 'Redressement fiscal', def: "Notification par l'administration d'une erreur ou omission. Vous avez 30 jours pour répondre (acceptation ou contestation). Sans réponse : l'administration impose son chiffrage." },
          { term: 'Recours hiérarchique', def: "Première étape de contestation : saisir le supérieur du vérificateur. Délai 30 jours. Mention obligatoire à la fin de la notification de redressement." },
          { term: 'Recours contentieux', def: 'Saisine du Tribunal administratif (impôts directs) ou du Tribunal judiciaire (TVA). Délais : 2 mois après réponse de l\'administration. Avocat spécialisé recommandé.' },
          { term: 'Majorations & intérêts', def: "Erreur involontaire : intérêts de retard (0,2 %/mois). Manquement délibéré : +40 %. Manœuvres frauduleuses : +80 %. Pénalités cumulables avec amende pénale." },
        ],
      },

      // ── 5. Droit international du transport ───────────────────────
      {
        type: 'definitions',
        title: '5. Droit international du transport',
        items: [
          { term: 'CMR', def: 'Convention relative au contrat de transport international de Marchandises par Route (1956). S\'applique automatiquement à tout transport international où expéditeur ou destinataire est dans un pays signataire.' },
          { term: 'Lettre de voiture CMR', def: 'Document obligatoire en 3 originaux (expéditeur / destinataire / transporteur) qui matérialise le contrat. Sans CMR : la convention s\'applique quand même mais la preuve devient compliquée.' },
          { term: 'Incoterms 2020', def: "11 termes commerciaux internationaux (EXW, FCA, CPT, CIP, DAP, DPU, DDP, FAS, FOB, CFR, CIF). Définissent qui paie quoi, qui assume les risques, où s'opère le transfert." },
          { term: 'EXW (Ex Works)', def: "Vendeur met la marchandise à disposition dans son usine. Acheteur prend en charge TOUT (chargement, transport, douane, assurance, livraison). Minimum d'obligations pour le vendeur." },
          { term: 'DDP (Delivered Duty Paid)', def: "Vendeur livre marchandise dédouanée à destination, droits & taxes payés. Maximum d'obligations pour le vendeur. Souvent évité car risqué côté fiscal." },
          { term: 'T-Form (T1/T2)', def: 'Document de transit douanier UE. T1 : marchandise non-communautaire (en transit). T2 : marchandise communautaire en circulation. Cautionnement bancaire requis.' },
          { term: 'Autoliquidation TVA intracommunautaire', def: 'Mécanisme par lequel l\'acheteur (assujetti EU) liquide la TVA dans son pays au lieu que le vendeur la facture. Numéro de TVA intra-EU des deux parties OBLIGATOIRE.' },
        ],
      },
      {
        type: 'formulas',
        title: 'Mécaniques CMR & Incoterms',
        items: [
          {
            name: 'Limitation de responsabilité CMR',
            formula: 'Indemnité max = poids brut endommagé (kg) × 8,33 DTS',
            example: "Une palette de 800 kg détruite dans un sinistre. Indemnité max = 800 × 8,33 DTS = 6 664 DTS ≈ 8 200 € (1 DTS ≈ 1,23 €). Au-delà : le transporteur n'est PAS responsable, sauf clause spéciale ou dol.",
          },
          {
            name: 'Transfert des risques (Incoterm CIF)',
            formula: 'Risques transférés au passage du bastingage du navire au port d\'embarquement',
            example: "Vente CIF Marseille → Mumbai. Marchandise endommagée pendant la traversée : c'est l'ACHETEUR qui supporte la perte (à charge pour lui d'actionner l'assurance maritime). Vendeur dégagé.",
          },
          {
            name: "Cautionnement T1",
            formula: 'Caution bancaire ≈ valeur marchandise × 25 % (droits + TVA EU)',
            example: "Transit T1 de 50 000 € de marchandises (textile asiatique vers Hambourg). Caution requise ≈ 12 500 € bloquée à la banque pendant le transit, libérée à présentation de la quittance d'apurement.",
          },
        ],
      },

      // ── 6. Responsabilité dirigeant ────────────────────────────────
      {
        type: 'definitions',
        title: '6. Responsabilité civile & pénale du dirigeant',
        items: [
          { term: 'Responsabilité civile', def: 'Obligation de réparer un dommage causé. Couverte par une assurance RCMS (Responsabilité Civile des Mandataires Sociaux) — ~150-400 €/mois selon la taille. INDISPENSABLE.' },
          { term: 'Responsabilité pénale', def: "Sanctionne les infractions personnelles du dirigeant (abus de bien social, travail dissimulé, escroquerie, blessures involontaires). NON couverte par les assurances classiques." },
          { term: 'Faute de gestion', def: 'Acte ou omission contraire à l\'intérêt social. Engage la responsabilité du dirigeant en cas de procédure collective (action en comblement de passif).' },
          { term: 'Action en comblement de passif', def: "Si l'entreprise est en liquidation et que les fautes de gestion ont contribué à l'insuffisance d'actif, le tribunal peut condamner le dirigeant à payer tout ou partie des dettes sur son patrimoine personnel." },
          { term: "Délit d'entrave", def: "Empêcher le fonctionnement du CSE, refuser la convocation aux réunions, refuser la communication d'informations. Sanction : 7 500 € d'amende + 1 an de prison max." },
          { term: 'Travail dissimulé', def: "Non-déclaration d'un salarié ou d'heures travaillées. 45 000 € d'amende + 3 ans de prison. Sanction systématique : interdiction de gérer pendant 5 ans + exclusion des aides publiques." },
        ],
      },
      {
        type: 'cases',
        title: 'Cas pratique — Responsabilité après accident grave',
        items: [
          {
            title: "Un chauffeur cause un accident mortel — quelle responsabilité pour vous ?",
            situation: "Un de vos chauffeurs provoque un accident mortel suite à un dépassement du temps de conduite (chronotachygraphe truqué). Une enquête est ouverte.",
            solution: "Vous êtes pénalement responsable si l'enquête prouve : (1) que vous saviez ou auriez dû savoir (audits chronos absents), (2) que vous avez tacitement encouragé le dépassement (objectifs de tournée impossibles). **Sanction max : 10 ans de prison + 150 000 € d'amende + interdiction de gérer + responsabilité civile illimitée**. Votre RCMS ne couvre PAS le pénal.",
            lesson: "Auditez les chronos 1 fois par trimestre. Documentez chaque sensibilisation chauffeur. Mieux vaut un refus motivé d'une tournée trop chargée qu'un drame couvert sur 10 ans.",
          },
          {
            title: "Action en comblement de passif après liquidation",
            situation: "Votre société est en liquidation. Le mandataire judiciaire identifie 3 « fautes de gestion » : continuité d'activité 6 mois après la cessation de paiements, rémunération du dirigeant maintenue, opérations entre sociétés du même groupe non documentées.",
            solution: "Le tribunal peut vous condamner à combler tout ou partie de l'insuffisance d'actif sur votre **patrimoine personnel** (résidence principale incluse). Pour vous protéger en amont : (1) déclarer la cessation de paiements au plus tard 45 jours après les faits, (2) baisser sa propre rémunération avant tout licenciement, (3) formaliser par convention TOUS les flux intra-groupe.",
            lesson: "L'action en comblement de passif est la sanction la plus lourde pour un dirigeant. Le seul antidote : agir vite, transparemment, et documenter au fil de l'eau.",
          },
        ],
      },

      // ── Vocabulaire transverse ─────────────────────────────────────
      {
        type: 'vocabulary',
        title: 'Vocabulaire juridique transverse',
        items: [
          { word: "Acte d'huissier", def: "Document officiel notifié par un huissier de justice. Preuve incontestable de remise. Point de départ des délais de procédure." },
          { word: "Assignation",     def: "Acte d'huissier convoquant une partie devant un tribunal. Indique la juridiction, la date, et les prétentions adverses." },
          { word: 'Astreinte',       def: "Pénalité financière journalière prononcée par un juge pour forcer l'exécution d'une décision. Exemple : 500 €/jour de retard." },
          { word: 'CSE',             def: "Comité Social et Économique. Obligatoire dès 11 salariés. Représentation du personnel, consultation sur les orientations stratégiques, droit d'alerte." },
          { word: 'CDA',             def: "Convention Demandeur d'emploi (Pôle emploi). Aide à l'embauche jusqu'à 50 % du salaire pendant 6-12 mois selon profil." },
          { word: 'DREAL',           def: "Direction Régionale de l'Environnement, de l'Aménagement et du Logement. Contrôle TRM (Transport Routier de Marchandises) en France." },
          { word: 'Mise en demeure', def: "Sommation formelle d'exécuter une obligation sous délai. Point de départ des intérêts de retard et des pénalités. Toujours répondre dans les délais." },
          { word: 'OPC',             def: "Officier de Police Judiciaire. Habilité à interroger, perquisitionner, placer en garde à vue dans le cadre d'une enquête pénale." },
          { word: 'Recours suspensif', def: "Recours qui suspend l'exécution de la décision contestée. Rare en droit administratif. Demander explicitement le sursis." },
          { word: 'URSSAF',          def: "Organisme de recouvrement des cotisations sociales. Pouvoir de redressement, mise en recouvrement, saisie sur compte." },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 9. RISQUES & CRISES
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'crises',
    title: 'Risques & Crises',
    subtitle: 'Cessation, sinistre, grève, plan B',
    icon: 'AlertTriangle',
    color: '#DC2626',
    pages: [
      {
        type: 'cover',
        title: 'Risques & Crises',
        intro: "Une PME transport solide n'évite pas les crises — elle les détecte tôt et les surmonte. Trésorerie, RH, sinistres, grèves : voyons comment lire les signaux et structurer une réponse.",
        bullets: [
          'Cessation de paiements vs insolvabilité',
          'Grèves : impacts et leviers',
          'Plan de continuité d\'activité',
        ],
      },
      {
        type: 'definitions',
        title: 'Définitions essentielles',
        items: [
          { term: 'Cessation de paiements',     def: "Impossibilité de faire face au passif exigible avec l'actif disponible. Trésorerie < 0 + dettes échues → déclaration obligatoire sous 45 jours." },
          { term: 'Insolvabilité technique',    def: "Capitaux propres < -10 % de l'actif total ET trésorerie négative à la clôture mensuelle. Liquidation immédiate possible." },
          { term: 'Streak d\'insolvabilité',    def: "Jours consécutifs en zone dangereuse (tréso < 0 ET capitaux propres < 0). À 30 jours → liquidation par cessation de paiements." },
          { term: 'AGE (assemblée générale extraordinaire)', def: 'Outil de recapitalisation par apport associé. Disponible 1 fois. Injecte 50 % du déficit pour relancer.' },
          { term: 'Grève nationale',            def: "Mouvement social touchant tout le secteur. Revenus -50 à -100 % pendant la durée (1-3 jours typiques)." },
          { term: 'Sinistre',                   def: "Accident matériel ou corporel impliquant un véhicule de la flotte. Franchise assurance + perte d'exploitation pendant les réparations." },
          { term: 'Plan de continuité',         def: 'Procédure documentée pour maintenir l\'activité en cas de crise majeure (sinistre, grève, pandémie).' },
          { term: 'Mandataire judiciaire',      def: "Officier ministériel qui pilote la liquidation. Vend les actifs pour payer les créanciers selon un ordre légal strict." },
        ],
      },
      {
        type: 'formulas',
        title: 'Formules de calcul',
        items: [
          {
            name: 'Ratio d\'alerte trésorerie',
            formula: 'Runway = Trésorerie ÷ Charges fixes mensuelles\n• > 6 mois : sain\n• 3-6 mois : surveillance\n• < 3 mois : alerte rouge',
            example: "Trésorerie 12 000 € / Charges fixes 5 000 € → 2,4 mois. Action immédiate requise.",
          },
          {
            name: 'Coût d\'un sinistre',
            formula: "Coût = franchise + (jours d'immobilisation × revenu perdu) + perte de réputation",
            example: "Franchise 1 500 € + 15 j × 200 €/j = 1 500 + 3 000 = 4 500 € + impact réputation (-3 pts).",
          },
          {
            name: 'Impact financier d\'une grève',
            formula: 'Perte = Revenu_jour_moyen × jours_grève × (1 − part_revenu_maintenue)',
            example: "Revenu/j 800 € × 2 jours × (1 - 0,5) = 800 € perdus (si vous choisissez « Tenter livraisons urgentes »).",
          },
          {
            name: 'Apport minimum AGE',
            formula: 'Recap = 50 % × |capitaux propres déficit|',
            example: "Capitaux propres = -16 000 € → Recap minimum = 8 000 € à apporter pour stopper le streak.",
          },
        ],
      },
      {
        type: 'diagram',
        title: "Chronologie d'une cessation de paiements",
        intro: "Il existe TOUJOURS 4 étapes avant la liquidation. Chaque étape donne une chance de redresser. Apprenez à les reconnaître tôt.",
        diagram: 'crisis-timeline',
        legend: [
          { label: 'Signal',  desc: 'Runway < 3 mois. Aucune dette échue. Premiers KPIs en baisse.' },
          { label: 'Alerte',  desc: 'Trésorerie < 0. Streak commence à compter. Négociations bancaires urgentes.' },
          { label: 'Crise',   desc: 'Streak > 14 jours. AGE proposée, dernière chance avant déclaration.' },
          { label: 'Liq.',    desc: 'Streak >= 30 jours ou capitaux propres < -10 % de l\'actif. Tribunal de Commerce.' },
        ],
      },
      {
        type: 'cases',
        title: 'Cas pratiques',
        items: [
          {
            title: 'Streak 12 jours — que faire ?',
            situation: "Votre trésorerie est négative depuis 12 jours consécutifs. Capitaux propres : -8 000 €. Il vous reste 18 jours avant cessation officielle.",
            solution: "Plan 3 axes : (1) AGE pour 4 000 € minimum (50 % du déficit) = streak reset à 0. (2) Geler les embauches et reporter les achats discrétionnaires. (3) Appeler la banque pour un crédit court terme 10 k€ adossé à vos créances clients (cession Dailly).",
            lesson: "L'AGE est votre joker — utilisez-la quand toutes les autres options ont été tentées, pas en premier réflexe.",
          },
          {
            title: 'Grève nationale 3 jours',
            situation: "Une grève des transports est annoncée. Vous avez 4 contrats actifs avec délai.",
            solution: "Option 1 « Attendre » : 0 revenu × 3 jours, mais réputation +1 (solidarité). Option 2 « Tenter livraisons urgentes » : 50 % revenu maintenu × 3 jours + 200 € coût exceptionnel. Calcul : si revenu/j > 800 €, option 2 gagne 1 000 € net.",
            lesson: "Décidez selon votre runway. Si runway < 3 mois → tentez. Si runway > 6 mois → respectez la grève, ça compte pour les chauffeurs.",
          },
          {
            title: 'Sinistre majeur (camion détruit)',
            situation: "Votre camion 44 t (valeur 65 000 €) est détruit dans un accident. Assurance couvre 80 %. Immobilisation prévue 3 semaines avant remplacement.",
            solution: "Cash immédiat : 52 000 € d'indemnité - 1 500 € franchise = 50 500 €. Perte d'exploitation 21 j × 300 € = 6 300 €. Action : louer un camion équivalent 800 €/sem × 3 = 2 400 €. Bilan : trou de ~2 200 € + 4 j de réorga.",
            lesson: "Souscrivez TOUJOURS une garantie « perte d'exploitation » (~200 €/mo). Couvre l'immobilisation et évite 4 000-8 000 € de gap.",
          },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Pièges à éviter',
        items: [
          {
            title: 'Recapitaliser sans changer le modèle',
            mistake: "Vous injectez 10 000 € via AGE. 60 jours plus tard, vous êtes de nouveau en zone rouge.",
            signal: 'Capitaux propres redeviennent négatifs < 90 jours après une recapitalisation.',
            fix: "L'AGE doit S'ACCOMPAGNER d'une décision structurelle : céder un camion sous-utilisé, sortir un contrat à marge négative, ou supprimer une charge fixe (sous-location partielle dépôt, par ex).",
          },
          {
            title: 'Cacher la situation aux fournisseurs',
            mistake: "Vous évitez le banquier et le fournisseur carburant pour ne pas alerter. 30 jours après, plus d'essence et un huissier à la porte.",
            signal: 'Vous ne décrochez plus les appels du banquier ou comptable.',
            fix: "La transparence négociée 30 jours plus tôt sauve une PME sur deux. Appelez votre banquier le jour où vous voyez la runway passer sous 3 mois.",
          },
          {
            title: 'Sur-réagir à un sinistre isolé',
            mistake: "Un seul accident, vous changez d'assurance, vendez le camion accidenté, licenciez le chauffeur impliqué — sans investigation.",
            signal: "Décisions multiples en 7 jours suite à un événement unique.",
            fix: "Délai de 30 jours minimum avant toute décision structurelle post-sinistre. Investiguez la CAUSE (fatigue chauffeur, maintenance ratée, météo). La réaction émotionnelle coûte plus cher que le sinistre.",
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Vocabulaire',
        items: [
          { word: 'Cession Dailly',        def: "Mécanisme bancaire qui transforme vos créances clients en cash immédiat (avec décote ~2-5 %)." },
          { word: 'Conciliation',          def: 'Procédure amiable et confidentielle de négociation avec les créanciers, sous égide d\'un conciliateur judiciaire.' },
          { word: 'Concordat',             def: 'Plan de remboursement étalé négocié avec les créanciers, validé par le tribunal.' },
          { word: 'Force majeure',         def: 'Événement imprévisible et insurmontable (pandémie, catastrophe). Suspend les obligations contractuelles.' },
          { word: 'Mise en demeure',       def: 'Acte formel exigeant une action sous délai. Point de départ de la prescription / des pénalités.' },
          { word: 'Procédure de sauvegarde', def: 'Outil préventif (avant cessation de paiements). Donne 6 mois de gel des dettes pour réorganiser.' },
          { word: 'Redressement judiciaire', def: "Procédure curative (après cessation de paiements). Plan sur 10 ans max, supervisé par le tribunal." },
          { word: 'TGI',                    def: 'Tribunal de Grande Instance — désormais Tribunal Judiciaire. Compétent pour les procédures collectives.' },
        ],
      },
    ],
  },
];

/**
 * Auto-split long item-based pages so each page fits inside the visual
 * 520×720 page format of the flipbook (~620 px usable body height after
 * header + footer). User feedback (28/05/2026) showed that 3-item cases
 * / pitfalls AND 4-item formulas / 8-item definitions all overflowed the
 * cream paper area in react-pageflip. We mutate `MANUAL_CHAPTERS` in
 * place so book + index lookups + tests see the same paginated structure.
 *
 * Caps are tuned per page type based on average vertical footprint:
 *   - cases & pitfalls  : 2 (big cards, ~250 px each)
 *   - formulas          : 3 (cards w/ pre-formatted formula + example)
 *   - definitions       : 5 (numbered list, ~80 px each)
 *   - vocabulary        : 7 (inline dt/dd, ~50 px each)
 */
const MAX_ITEMS_PER_PAGE = {
  cases: 2,
  pitfalls: 2,
  formulas: 3,
  definitions: 5,
  vocabulary: 7,
};
MANUAL_CHAPTERS.forEach((chapter) => {
  const splitPages = [];
  chapter.pages.forEach((page) => {
    const cap = MAX_ITEMS_PER_PAGE[page.type];
    if (!cap || !Array.isArray(page.items) || page.items.length <= cap) {
      splitPages.push(page);
      return;
    }
    // Slice the items array into chunks of `cap`. Subsequent chunks get a
    // `(suite N/M)` suffix on the title so the apprenant knows the section
    // continues on the next page.
    const chunks = [];
    for (let i = 0; i < page.items.length; i += cap) {
      chunks.push(page.items.slice(i, i + cap));
    }
    chunks.forEach((chunk, idx) => {
      splitPages.push({
        ...page,
        items: chunk,
        title: idx === 0
          ? page.title
          : `${page.title} (suite ${idx + 1}/${chunks.length})`,
      });
    });
  });
  chapter.pages = splitPages;
});

/** Total pages count (used by the book). */
export const TOTAL_MANUAL_PAGES = MANUAL_CHAPTERS.reduce(
  (sum, c) => sum + c.pages.length,
  0,
);

/** Find page by absolute index (0-based). Returns { chapter, page, index }. */
export function getManualPageByIndex(idx) {
  let cursor = 0;
  for (const chapter of MANUAL_CHAPTERS) {
    for (let i = 0; i < chapter.pages.length; i++) {
      if (cursor === idx) return { chapter, page: chapter.pages[i], chapterPageIndex: i };
      cursor++;
    }
  }
  return null;
}

/** Get the starting absolute index for a given chapter id (for jumps). */
export function getChapterStartIndex(chapterId) {
  let cursor = 0;
  for (const chapter of MANUAL_CHAPTERS) {
    if (chapter.id === chapterId) return cursor;
    cursor += chapter.pages.length;
  }
  return 0;
}

/* ─────────────────────────────────────────────────────────────────────
 * 31/05/2026 — i18n EN of the pedagogical manual.
 *
 * The English mirror lives in `./manualContentEN.js` with the SAME shape.
 * We re-apply the same auto-pagination logic and expose a locale-aware
 * getter so consumers can switch FR / EN without refactoring everything.
 * ───────────────────────────────────────────────────────────────────── */
import { MANUAL_CHAPTERS_EN as _RAW_MANUAL_CHAPTERS_EN } from './manualContentEN';

// Apply the same per-page item cap to the EN mirror so pagination matches
// the FR layout. We clone the chapters defensively (no mutation leak back).
const MANUAL_CHAPTERS_EN = _RAW_MANUAL_CHAPTERS_EN.map(chapter => {
  const splitPages = [];
  chapter.pages.forEach(page => {
    const cap = MAX_ITEMS_PER_PAGE[page.type];
    if (!cap || !Array.isArray(page.items) || page.items.length <= cap) {
      splitPages.push(page);
      return;
    }
    const chunks = [];
    for (let i = 0; i < page.items.length; i += cap) {
      chunks.push(page.items.slice(i, i + cap));
    }
    chunks.forEach((chunk, idx) => {
      splitPages.push({
        ...page,
        items: chunk,
        title: idx === 0
          ? page.title
          : `${page.title} (cont. ${idx + 1}/${chunks.length})`,
      });
    });
  });
  return { ...chapter, pages: splitPages };
});

/**
 * Locale-aware selector. Returns the FR or EN chapters array based on the
 * language passed in. Defaults to FR.
 */
export function getManualChapters(lang) {
  return lang === 'en' ? MANUAL_CHAPTERS_EN : MANUAL_CHAPTERS;
}

/** Total pages for the EN manual (useful for tests / PDF gen). */
export const TOTAL_MANUAL_PAGES_EN = MANUAL_CHAPTERS_EN.reduce(
  (sum, c) => sum + c.pages.length,
  0,
);

/** Locale-aware version of {@link getManualPageByIndex}. */
export function getManualPageByIndexLang(idx, lang) {
  const chapters = getManualChapters(lang);
  let cursor = 0;
  for (const chapter of chapters) {
    for (let i = 0; i < chapter.pages.length; i++) {
      if (cursor === idx) return { chapter, page: chapter.pages[i], chapterPageIndex: i };
      cursor++;
    }
  }
  return null;
}

/** Locale-aware version of {@link getChapterStartIndex}. */
export function getChapterStartIndexLang(chapterId, lang) {
  const chapters = getManualChapters(lang);
  let cursor = 0;
  for (const chapter of chapters) {
    if (chapter.id === chapterId) return cursor;
    cursor += chapter.pages.length;
  }
  return 0;
}

/** Locale-aware total pages getter. */
export function getTotalManualPages(lang) {
  return lang === 'en' ? TOTAL_MANUAL_PAGES_EN : TOTAL_MANUAL_PAGES;
}
